import React, { useRef, useEffect, useLayoutEffect, useState, useCallback, useMemo, Suspense } from 'react';
import { Message, User, Attachment, Channel, Reaction, MentionInput, MessageMention, ScheduledMessage, OnlineUser, DEFAULT_AVATAR } from '../types';
import { dbService } from '../services/dbService';
import { socketService, useTypingIndicator } from '../services/socketService';
import { useChannelMembers } from '../services/channelService';
import { useAffiliateUserSearch } from '../hooks/useAffiliateUserSearch';
import { MentionAutocomplete, MentionCandidate } from './MentionAutocomplete';
import { Send, Image as ImageIcon, ImageOff, Smile, Trash2, MoreVertical, Search, X, ChevronUp, ChevronDown, Paperclip, Download, ArrowDown, Monitor, Reply, Copy, CalendarDays, Pencil, Megaphone, Forward, Check, ListChecks, MessageCircle, Bookmark, Clock, Printer, CalendarPlus } from 'lucide-react';
import type { EmojiClickData, Theme, EmojiStyle } from 'emoji-picker-react';
import { isTauri } from '../utils/tauri';
import { formatDateLabel, isDifferentDate } from '../utils/date';
import { formatFileSize } from '../utils/formatFileSize';
import { getUploadPolicy, useUploadPolicy } from '../services/uploadPolicyService';
import {
  isAttachmentExpired,
  isExpiringSoon,
  formatExpiryLabel,
  formatRetentionNotice,
  EXPIRED_FILE_TEXT,
  EXPIRED_IMAGE_TEXT,
  EXPIRED_IMAGE_SHORT_TEXT,
  EXPIRED_DOWNLOAD_TOAST,
} from '../utils/attachmentExpiry';
import { isBlockedFileName } from '../services/blockedExtensionService';
import { iconForFileName } from '../utils/fileIcon';
import { resolveServerUrl } from '../utils/serverUrl';
import { printImages, PRINT_IMAGE_FAILED_TOAST } from '../utils/printImage';
import { splitByUrl } from '../utils/linkify';
import {
  extractClipboardPaste,
  getMessageBlocks,
  getComposerBlocks,
  replaceBlockText,
  removeBlock,
  normalizeMessageText,
  textForClipboard,
  looksLikeTable,
  setCell,
  rowsToTsv,
  TABLE_MAX_ROWS,
  TABLE_MAX_COLS,
  type ClipboardTable,
} from '../utils/messageTable';
import { MessageTable } from './chat/MessageTable';
import { ComposerTableGrid } from './chat/ComposerTableGrid';
import { openExternalUrl } from '../utils/openExternal';
import { ScreenCaptureOverlay } from './ScreenCaptureOverlay';
import { useToast } from '../contexts/ToastContext';
import { ImageLightbox, LightboxSlide } from './ImageLightbox';
import { ContextMenu } from './ContextMenu';
import { requestOpenEditor } from '../services/calendarService';
import { ConfirmDialog } from './ConfirmDialog';
import { ScheduleComposerModal, type ScheduleSeed, type ScheduleBody } from './ScheduleComposerModal';
import type { ScheduledMessageBody } from '../services/scheduledMessageService';
import {
  screenAttachmentFiles,
  pastedImageFiles,
  blockedAttachmentMessage,
  truncateFileName,
} from '../utils/attachmentScreening';
import { useLongPress } from '../hooks/useLongPress';
import { MessageItem, REACTION_EMOJIS, hasDisplayText } from './chat/MessageItem';
// 주 입력이 터치인 기기(폰·태블릿)에서는 Enter=줄바꿈, 전송은 버튼으로만.
// 화면 폭이 아니라 (pointer: coarse) 기준 — iPad는 넓어도 터치이고, 터치스크린 노트북(주 포인터 fine)은 Enter=전송 유지
import { IS_COARSE_POINTER } from '../utils/pointer';
import { draftKeyOf, readDraft, saveDraft, clearDraft, emptyDraft, type DraftSnapshot } from '../utils/chatDraft';

// 이모지 데이터가 커서 메인 번들에서 분리 (첫 클릭 시 로드)
const EmojiPicker = React.lazy(() => import('emoji-picker-react'));

// 편집 중일 때 초안 미러에 싣는 빈 원장 — 편집 대상의 멘션은 초안의 것이 아니다.
// 읽기 전용으로만 쓴다(chatDraft.saveDraft가 복사해 담는다)
const EMPTY_MENTIONS: Map<string, MentionInput> = new Map();

/**
 * 컴포저 안에서 지금 커서가 있는 곳. blockIndex가 null이면 표가 없는 평소 상태(= 예전 동작)다.
 * 멘션 삽입·이모지 삽입·붙여넣기 세 소비처가 이것 하나를 공유한다.
 */
type ComposerFocus =
  | { kind: 'textarea'; blockIndex: number | null; el: HTMLTextAreaElement }
  | { kind: 'cell'; blockIndex: number; r: number; c: number; el: HTMLInputElement };

const MIME_MAP: Record<string, string> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp',
  pdf: 'application/pdf', doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain', hwp: 'application/x-hwp', zip: 'application/zip',
  '7z': 'application/x-7z-compressed', mp4: 'video/mp4', mov: 'video/quicktime', avi: 'video/x-msvideo',
};
function getMimeType(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  return MIME_MAP[ext] || 'application/octet-stream';
}

// 붙여넣기 이미지 판정(PASTE_IMAGE_TYPES)·첨부 사전 차단·차단 안내 문구는
// src/utils/attachmentScreening.ts로 옮겼다 (예약 모달과 공용).

// 전달 다중 선택 상한 (서버 POST /api/messages/forward 계약과 동일)
const MAX_FORWARD_SELECT = 20;

// 화면 캡처는 getDisplayMedia(휴대폰 브라우저에는 없다) + 마우스 드래그 영역 선택(ScreenCaptureOverlay)이라
// 둘 중 하나라도 안 되는 기기에서는 메뉴를 감춘다(화상통화 '화면 공유' 버튼과 같은 방식).
// 휴대폰은 기기 스크린샷을 찍어 '이미지 선택'으로 보낸다.
const CAN_SCREEN_CAPTURE = !IS_COARSE_POINTER
  && typeof (navigator.mediaDevices as { getDisplayMedia?: unknown } | undefined)?.getDisplayMedia === 'function';

// 파일 크기 표기는 src/utils/formatFileSize.ts로 옮겼다 (AttachmentDrawer·관리자 콘솔과 공용).

// 메시지 메뉴를 연 지점이 말풍선 사진이면 그 첨부 id(renderAttachments의 data-attachment-id).
// '인쇄' 항목이 그 1장만 뽑을지, 메시지의 사진 전부를 뽑을지 가른다.
function attachmentIdAt(target: EventTarget | null): string | undefined {
  return (target as Element | null)?.closest?.('[data-attachment-id]')?.getAttribute('data-attachment-id') ?? undefined;
}

// 메시지 메뉴 '인쇄'가 뽑을 사진 URL. 사진 위에서 열었으면 그 1장, 아니면 메시지의 사진 전부.
// 만료 사진은 원본이 지워져 404라 뺀다(빈 배열이면 메뉴 항목 자체가 없다).
function printableImageUrls(msg: Message, imageId?: string): string[] {
  if (msg.msgType === 'system' || msg.deletedAt) return [];
  const images = (msg.attachments || []).filter(
    (a) => a.type.startsWith('image/') && !isAttachmentExpired(a.expiresAt),
  );
  const picked = imageId ? images.filter((a) => String(a.id) === imageId) : [];
  return (picked.length > 0 ? picked : images).map((a) => resolveServerUrl(a.url));
}

// 첨부 URL의 서버 주소 결합은 utils/serverUrl의 resolveServerUrl 하나를 쓴다.
// 모듈 스코프 import라 참조가 안정적이다 — 컴포넌트 안에 두면 렌더마다 새로 만들어져
// 이를 참조하는 useCallback(첨부 렌더 등)이 매번 무효화된다.

interface ChatWindowProps {
  messages: Message[];
  currentUser: User;
  channel: Channel;
  /**
   * false를 돌려주면 전송이 실패한 것이므로 입력창·첨부를 비우지 않는다
   * (초안 DM의 첫 메시지에서 채널 생성이 실패하는 경우). void 반환은 성공으로 취급한다.
   */
  onSendMessage: (text: string, attachments?: Attachment[], replyToId?: number | null, mentions?: MentionInput[]) => Promise<boolean> | void;
  onReactionToggle?: (messageId: number, emoji: string) => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onDeleteMessage?: (messageId: number) => void;
  darkMode?: boolean;
  isSearchOpen?: boolean;
  onSearchOpenChange?: (open: boolean) => void;
  // 날짜 점프 (YYYY-MM-DD 선택 → 그 날짜 대화로 이동)
  onJumpToDate?: (dateStr: string) => void;
  jumpTarget?: { id: number; nonce: number } | null;
  hasMoreNewer?: boolean;
  onLoadNewer?: () => void;
  onReturnToLatest?: () => void;
  /** 메시지를 채널 공지로 등록. 미전달이면(권한 없음) 메뉴에 항목이 나오지 않는다 */
  onSetNotice?: (messageId: number) => void;
  /** 메시지 전달 — 대상 채널 선택 모달을 여는 App 콜백. 미전달이면 전달 진입점 미표시 */
  onForward?: (messages: Message[]) => void;
  /** 다중 선택 모드 진입 요청 (단건 전달 모달의 "여러 개 선택") — nonce 변경 시 ids preselect */
  forwardSelectRequest?: { ids: number[]; nonce: number } | null;
  /** 내 예약 메시지 전체(App의 myScheduled) — 이 방 것은 내부에서 필터. 미전달이면 예약 기능 비노출 */
  scheduledMessages?: ScheduledMessage[];
  /** 예약 등록(첨부는 이미 올린 파일) — false면 실패(모달·입력 유지, onSendMessage와 같은 계약) */
  onScheduleMessage?: (body: ScheduledMessageBody) => Promise<boolean>;
  /** 예약 수정 — 본인 소유 + 대기 중만. false면 실패(모달 유지) */
  onUpdateScheduledMessage?: (id: string, body: ScheduledMessageBody) => Promise<boolean>;
  /** 예약 취소(대기) 또는 삭제(실패 행) — 토스트는 App이 낸다 */
  onCancelScheduledMessage?: (item: ScheduledMessage) => void;
  /**
   * 발신자 사번 → 접속 상태 (말풍선 아바타의 점).
   * ⚠ App이 '보이는 발신자'만 추려 참조를 고정해 넘긴다 — onlineUsers 배열을 그대로 받으면
   * 사내 누가 접속만 해도 React.memo(ChatWindow)가 깨져 메시지 목록 전체가 리렌더된다.
   * 미전달이면 점을 그리지 않는다.
   */
  senderPresence?: Map<string, OnlineUser['status']>;
  /**
   * 말풍선 아바타 클릭 → 발신자 프로필 모달 (App의 openUserProfile — 모달의 '1:1 대화'가 기존 DM 또는 초안 DM을 연다).
   * 미전달이면 아바타가 눌리지 않는다. ⚠ 영상통화 사이드바에는 넘기지 않는다 — 모달의 '1:1 대화'가
   * 통화 중에 App의 activeChannel을 바꾸면 안 된다. 참조가 흔들리면 React.memo가 깨지므로 useCallback 필수.
   */
  onOpenProfile?: (user: User) => void;
}

const ChatWindowComponent: React.FC<ChatWindowProps> = ({ messages, currentUser, channel, onSendMessage, onReactionToggle, hasMore, onLoadMore, onDeleteMessage, darkMode, isSearchOpen: isSearchOpenProp, onSearchOpenChange, onJumpToDate, jumpTarget, hasMoreNewer, onLoadNewer, onReturnToLatest, onSetNotice, onForward, forwardSelectRequest, scheduledMessages, onScheduleMessage, onUpdateScheduledMessage, onCancelScheduledMessage, senderPresence, onOpenProfile }) => {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // 검색 관련 상태 (외부 prop과 브릿지)
  const { showToast } = useToast();
  // 첨부 보관 기간 안내(입력창 미리보기). 관리자가 바꾸면 소켓 전파로 즉시 반영된다.
  // ⚠ addFiles는 이 값을 절대 참조하지 않는다 — addFiles의 빈 deps가 깨지면 Tauri 드롭
  //    리스너가 정책이 바뀔 때마다 해제·재등록된다(모듈 동기 getter를 쓰는 이유).
  const { retentionDays } = useUploadPolicy();
  const retentionNotice = formatRetentionNotice(retentionDays);
  const [isSearchOpenInternal, setIsSearchOpenInternal] = useState(false);
  const isSearchOpen = isSearchOpenProp !== undefined ? isSearchOpenProp : isSearchOpenInternal;
  const setIsSearchOpen = (open: boolean) => {
    if (onSearchOpenChange) onSearchOpenChange(open);
    else setIsSearchOpenInternal(open);
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<number[]>([]); // 검색된 메시지 ID 목록
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  // 날짜 점프용 선택값 (YYYY-MM-DD)
  const [jumpDate, setJumpDate] = useState('');
  const todayStr = useMemo(() => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  }, []);
  const [highlightedMessageId, setHighlightedMessageId] = useState<number | null>(null);
  const [reactionPickerMsgId, setReactionPickerMsgId] = useState<number | null>(null);
  // 메시지 컨텍스트 메뉴 (모바일 long-press + 데스크톱 우클릭 공용, 좌표 기준 플로팅)
  // 메뉴는 손가락을 뗄 때(touchend) 연다 — 누른 상태에서 열면 손을 떼는 순간의
  // synthetic mouse 이벤트/touchcancel이 메뉴 닫기 감지에 걸려 즉시 닫히는 문제가 있음
  // via: backdrop·스크롤 차단은 연 경로가 터치일 때만 (IS_COARSE_POINTER 상수가 아닌
  // 경로 기준 — 터치스크린 노트북은 주 포인터가 fine이어도 롱프레스 가능)
  // imageId: 사진 위에서 열었으면 그 첨부 id — '인쇄'가 그 1장만 뽑는다(없으면 메시지의 사진 전부)
  const [msgMenu, setMsgMenu] = useState<{ msg: Message; isOwn: boolean; x: number; y: number; via: 'touch' | 'mouse'; imageId?: string } | null>(null);
  // 예약 전송 UI 상태 (핸들러·메뉴 배선은 handleSubmit 아래 '예약 전송' 블록).
  // seed = 입력창에서 넘겨받은 글·첨부·멘션 + 그때의 초안 키(성공 시 그 키의 초안을 지운다). 칩으로 열면 null
  const [scheduleModal, setScheduleModal] = useState<null | { seed: (ScheduleSeed & { draftKey: string }) | null }>(null);
  const [sendMenu, setSendMenu] = useState<{ x: number; y: number; via: 'touch' | 'mouse' } | null>(null);
  // 예약 창이 떠 있는 동안 Tauri 네이티브 드롭을 받을 자리 (ref라 드롭 리스너 identity가 바뀌지 않는다)
  const scheduleDropSinkRef = useRef<((files: File[]) => void) | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFiredRef = useRef(false); // 500ms 홀드 성립 여부
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);
  // 스와이프 답장 (모바일): 방향 잠금 성립 후 translateX 피드백, 임계값 도달 시 답장 모드.
  // 터치는 한 번에 하나라 공용 ref로 충분 (롱프레스와 공존: 정지=롱프레스, 수평=스와이프, 수직=스크롤)
  const swipeActiveRef = useRef(false);
  const swipeMsgIdRef = useRef<number | null>(null);
  const swipeDxRef = useRef(0);
  const SWIPE_TRIGGER_PX = 48;
  const SWIPE_MAX_PX = 72;
  // 자동 스크롤 콜백에서 최신 메뉴 상태를 읽기 위한 미러 ref (메뉴 열림 중 리스트 정지)
  const msgMenuOpenRef = useRef(false);
  useEffect(() => {
    msgMenuOpenRef.current = msgMenu !== null;
  }, [msgMenu]);
  const messageRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // 답장 대상 메시지
  const [replyTo, setReplyTo] = useState<Message | null>(null);

  // 편집 중인 메시지 (답장과 상호 배타). 편집 진입 시 작성 중이던 draft를 백업했다가 종료 시 복원
  const [editingMsg, setEditingMsg] = useState<Message | null>(null);
  const draftBackupRef = useRef('');

  // ----------------------------------------
  // 작성 중 초안(draft) — 채널별 보관·복원
  //
  // ChatWindow는 조건부 마운트라(App.tsx의 isMobile ? isMobileChatOpen : isDesktopChatOpen)
  // 방을 닫으면 state가 통째로 사라진다. 저장소는 컴포넌트 바깥(utils/chatDraft)에 있고
  // 여기서는 '무엇을 담을지'와 '언제 담을지'만 정한다.
  // ----------------------------------------
  const draftUserId = String(currentUser.id);
  const draftKey = draftKeyOf(channel);
  // 디바운스 타이머·이벤트 리스너가 읽는 현재 키. ⚠ 복원 effect 안에서만 갱신한다 —
  // 렌더 본문에서 갱신하면 전환 직후 한 렌더 동안 '이전 방 텍스트 + 새 방 키' 조합이 만들어져
  // 이전 글이 새 방 초안으로 넘어간다.
  const draftKeyRef = useRef(draftKey);
  // 렌더 시점 동기 미러 (대입은 pendingMentionsRef 선언 아래에 있다).
  // 언마운트 cleanup·디바운스 타이머가 낡은 클로저를 읽지 않게 하는 장치다.
  const composerRef = useRef<DraftSnapshot>(emptyDraft());

  // 전달 다중 선택 모드 (Phase 2) — 켜지면 롱프레스/스와이프/컨텍스트 메뉴를 초입 가드로 전부 차단하고
  // 행 탭=선택 토글(투명 오버레이). 입력 영역은 선택 액션바로 대체된다. 상한 20(서버 계약과 동일)
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // 제스처 핸들러는 deps 없는 useCallback이라(참조 고정이 memo의 전제) 최신 selectMode를
  // 클로저로 읽을 수 없다 — ref로 미러링한다.
  const selectModeRef = useRef(selectMode);
  useEffect(() => {
    selectModeRef.current = selectMode;
  }, [selectMode]);
  const toggleSelect = useCallback((msg: Message) => {
    if (msg.deletedAt || msg.msgType === 'system') return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(msg.id)) {
        next.delete(msg.id);
      } else {
        if (next.size >= MAX_FORWARD_SELECT) return prev;
        next.add(msg.id);
      }
      return next;
    });
  }, []);
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };
  // 단건 전달 모달의 "여러 개 선택" → App이 nonce를 올려 선택 모드 진입 요청 (해당 메시지 preselect)
  useEffect(() => {
    if (!forwardSelectRequest) return;
    setSelectMode(true);
    setSelectedIds(new Set(forwardSelectRequest.ids));
    setMsgMenu(null);
  }, [forwardSelectRequest?.nonce]); // eslint-disable-line
  // Escape로 선택 모드 해제 — handleKeyDown은 textarea 전용이라 window 리스너 별도 등록
  useEffect(() => {
    if (!selectMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') exitSelectMode();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectMode]);

  // 이모지 피커 상태
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // 표를 붙여넣으면 컴포저가 블록(텍스트/표)으로 쪼개져 textarea가 여러 개가 된다.
  // ⚠ 맨 아래 textarea는 언제나 textareaRef다 — 표가 없을 때 DOM이 예전과 완전히 같아야 하고,
  //   startEdit·전송 후 포커스 복원이 그 가정 위에 서 있다. 이 Map은 '위에 쌓인' 텍스트 블록만 담는다.
  const textareaRefs = useRef(new Map<number, HTMLTextAreaElement>());
  // 멘션·이모지 삽입이 "지금 어디에 커서가 있는지"를 알아야 한다. 소비처마다 ref를 늘리면
  // 다음 기능에서 반드시 하나가 빠진다 — 하나로 모은다.
  const composerFocusRef = useRef<ComposerFocus | null>(null);
  // 렌더 본문 미러 (초안 미러와 같은 자리·같은 이유 — effect로 미루면 한 박자 늦는다)
  const inputValueRef = useRef('');
  // 편집 진입 시 정규화된 본문. 이걸 안 두면 표 메시지를 열었다 그냥 저장만 해도
  // 정규화 차이 때문에 수정 emit이 나가 '(수정됨)'이 붙는다.
  const editBaselineRef = useRef('');
  // 셀을 다 비워 표 판정이 풀리려 할 때의 안내 (멘션 고지와 같은 슬롯을 쓴다)
  const [tableNotice, setTableNotice] = useState<string | null>(null);
  const tableNoticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 파일 첨부 상태
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  // addFiles는 deps가 비어 있어야 한다(Tauri 드롭 리스너가 addFiles를 의존성으로 갖는다 — 재생성되면
  // 첨부할 때마다 네이티브 리스너가 해제·재등록된다). 개수 상한 검사에 필요한 현재 첨부 수만 ref로 읽는다.
  const attachmentCountRef = useRef(0);
  const [pendingFiles, setPendingFiles] = useState<Map<string, File>>(new Map()); // 업로드 대기 파일
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingNewer, setIsLoadingNewer] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageFileInputRef = useRef<HTMLInputElement>(null);

  // 드래그앤드롭 상태
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  // 이미지 메뉴 상태
  const [showImageMenu, setShowImageMenu] = useState(false);
  const imageMenuRef = useRef<HTMLDivElement>(null);

  // 화면 캡처 상태
  const [isCapturing, setIsCapturing] = useState(false);
  const [capturedScreenshot, setCapturedScreenshot] = useState<string | null>(null);

  // 이미지 라이트박스 상태
  const [lightbox, setLightbox] = useState<{
    open: boolean;
    slides: LightboxSlide[];
    index: number;
  }>({ open: false, slides: [], index: 0 });

  // 채팅방 전환 — 떠나는 방의 작성 중 내용을 보관하고, 들어가는 방의 초안을 되살린다.
  //
  // ⚠ deps가 channel.id가 아니라 draftKey인 이유: 초안 DM은 상대가 누구든 id가 0이라
  //   이 effect가 돌지 않아 A에게 쓰던 글이 B의 입력창에 그대로 남았다.
  // ⚠ cleanup은 채널 전환과 언마운트(데스크톱 X, 모바일 뒤로가기) 양쪽에서 돈다 —
  //   클로저가 잡은 draftKey 덕에 항상 '떠나는 방'에 저장된다.
  useEffect(() => {
    const d = readDraft(draftUserId, draftKey);
    draftKeyRef.current = draftKey;
    setInputValue(d?.text ?? '');
    setReplyTo(d?.replyTo ?? null);
    setAttachments(d?.attachments ?? []);
    setPendingFiles(d?.pendingFiles ?? new Map());
    pendingMentionsRef.current = new Map(d?.mentions ?? []);
    // ⚠ 미러도 같은 자리에서 갱신한다. setState는 다음 렌더에 반영되는데 StrictMode의
    //   mount→cleanup→mount 사이에는 리렌더가 없어, 미러가 비어 있으면 cleanup이
    //   '빈 초안'을 저장(=삭제)해 방금 되살린 글을 지운다.
    composerRef.current = {
      text: d?.text ?? '',
      replyTo: d?.replyTo ?? null,
      mentions: new Map(d?.mentions ?? []),
      attachments: d?.attachments ?? [],
      pendingFiles: d?.pendingFiles ?? new Map(),
    };
    // 아래는 초안이 아니라 UI 상태 — 방을 옮기면 항상 초기화한다
    setShowEmojiPicker(false);
    setIsUploading(false);
    setUploadProgress(0);
    setEditingMsg(null);
    draftBackupRef.current = '';
    setMsgMenu(null);
    setMentionQuery(null);
    setOutsiderNotice(null);
    setSelectMode(false);
    setSelectedIds(new Set());
    // 예약 UI도 방 전환 시 닫는다 — 이전 방의 예약을 새 방 컨텍스트로 저장하는 사고 방지
    // (초안 DM이 첫 예약으로 실제 방이 되는 순간에도 키가 바뀌어 닫힌다 — 예약 자체는 성공하고 토스트가 뜬다)
    setScheduleModal(null);
    setSendMenu(null);
    return () => {
      saveDraft(draftUserId, draftKey, composerRef.current);
    };
  }, [draftKey, draftUserId]);

  // localStorage 반영은 디바운스한다 — 메모리 층은 위 미러라 이미 최신이고, 타이핑마다
  // JSON.stringify + setItem을 돌릴 이유가 없다. 타이머가 draftKeyRef를 읽는 이유는
  // 발화 시점의 방에 쓰기 위해서다(전환 중 어긋남 방지).
  useEffect(() => {
    const t = setTimeout(() => saveDraft(draftUserId, draftKeyRef.current, composerRef.current), 800);
    return () => clearTimeout(t);
  }, [draftUserId, draftKey, inputValue, attachments, pendingFiles, replyTo, editingMsg]);

  // 탭 전환·앱 백그라운드·창 닫기 — 언마운트 cleanup이 돌지 않는 이탈 경로를 메운다
  useEffect(() => {
    const flush = () => saveDraft(draftUserId, draftKeyRef.current, composerRef.current);
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
    };
  }, [draftUserId]);

  const startEdit = (msg: Message) => {
    if (!editingMsg) draftBackupRef.current = inputValue; // 연속 편집 진입 시 최초 draft 유지
    setEditingMsg(msg);
    setReplyTo(null);
    // 표가 든 메시지는 여기서 격자로 열린다 — 블록으로 나눴다 합쳐도 같은 모양이 되게 한 번 정리한다.
    // 탭이 없는 메시지(절대다수)는 normalizeMessageText가 원문을 그대로 돌려주므로 동작 변화가 없다.
    const normalized = normalizeMessageText(msg.text);
    editBaselineRef.current = normalized;
    setInputValue(normalized);
    // 원본 메시지의 멘션을 원장에 복원 — 편집 후 재전송 시 재인덱스에 반영.
    // dept의 userIds는 서버에 없으므로 빈 배열로 두고 submit 시 조직도에서 재충전.
    // 폐지된 'here'는 복원하지 않는다 — 편집·저장하면 그 멘션은 평문으로 남는다(재알림은 원래 없음)
    pendingMentionsRef.current = new Map();
    (msg.mentions || []).forEach((m: MessageMention) => {
      if (m.type === 'user' && m.targetId) {
        pendingMentionsRef.current.set(m.name, { type: 'user', userId: m.targetId, name: m.name });
      } else if (m.type === 'dept') {
        pendingMentionsRef.current.set(m.name, { type: 'dept', name: m.name, userIds: [] });
      } else if (m.type === 'all') {
        pendingMentionsRef.current.set('all', { type: 'all' });
      }
    });
    textareaRef.current?.focus();
  };

  const cancelEdit = () => {
    setEditingMsg(null);
    setInputValue(draftBackupRef.current);
    draftBackupRef.current = '';
    pendingMentionsRef.current.clear();
    setMentionQuery(null);
  };

  // public 채널은 Admin Console에서만 메시지 발송 가능
  const isReadOnly = channel.type === 'public';

  // ----------------------------------------
  // 멘션(@) 자동완성 (Phase 2)
  // dm/self는 멘션이 무의미하므로 비활성. 소스는 채널 멤버(1순위) + 계열사 조직도(2순위, lazy)
  // ----------------------------------------
  const mentionEnabled = !isReadOnly && channel.type !== 'dm' && channel.type !== 'self';
  // null = 팝업 닫힘, ''부터는 @ 뒤 필터어
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  // 조직도 로딩 lazy 래치 — 첫 팝업 오픈 시 활성화 후 유지 (HomeScreen searchActivated 패턴)
  const [mentionSourceActive, setMentionSourceActive] = useState(false);
  // 비멤버 멘션 선택 시 인라인 고지 (알림 미발송 안내)
  const [outsiderNotice, setOutsiderNotice] = useState<string | null>(null);
  const outsiderNoticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 선택된 멘션 원장: 표시명 → 전송용 구조. submit 시 최종 텍스트를 재스캔해 살아남은 것만 확정
  const pendingMentionsRef = useRef<Map<string, MentionInput>>(new Map());

  // 초안 미러 — 렌더 시점에 동기로 채운다.
  // ⚠ effect 미러로 옮기지 말 것: 커밋이 한 박자 늦어, 초안 DM이 실제 방으로 승격되는 순간
  //   cleanup이 방금 보낸 텍스트를 다시 초안으로 저장한다.
  // 편집 중에는 inputValue가 초안이 아니라 남의 메시지 원문이다(startEdit) — 백업본을 담는다.
  composerRef.current.text = editingMsg ? draftBackupRef.current : inputValue;
  composerRef.current.replyTo = replyTo;
  composerRef.current.attachments = attachments;
  composerRef.current.pendingFiles = pendingFiles;
  composerRef.current.mentions = editingMsg ? EMPTY_MENTIONS : pendingMentionsRef.current;
  inputValueRef.current = inputValue;

  // ── 컴포저 블록 ──────────────────────────────────────────────────────────
  // 입력창은 inputValue를 블록으로 나눠 그린다. 표를 별도 state로 빼지 않는 이유:
  // (1) 보이는 것과 보내지는 것이 같은 함수로 결정돼 왕복 일치가 구조적으로 보장되고
  // (2) 채널 전환·전송·편집 취소의 초기화 5곳이 이미 setInputValue라 추가 코드가 없으며
  // (3) 초안 미러(위)·handleSubmit·예약 전송이 전부 문자열 하나만 읽어 무변경이다.
  // ⚠ getMessageBlocks(캐시본)를 쓰면 안 된다 — 키 입력마다 새 키가 LRU 300칸을 밀어내
  //   말풍선 표의 React.memo가 죽는다. 컴포저는 캐시 없는 형제 진입점을 쓴다.
  const composerBlocks = useMemo(() => getComposerBlocks(inputValue), [inputValue]);

  // 맨 아래 textarea가 문자열의 어디에 해당하는가.
  //  plain   = 표 없음. 예전과 완전히 같은 단일 textarea
  //  block   = 마지막 블록이 텍스트. 그 블록을 담당한다
  //  virtual = 마지막 블록이 표. 표 뒤에 이어 쓸 빈 칸을 보여준다(입력이 생기면 실제 블록이 된다)
  const bottom = useMemo(() => {
    if (!composerBlocks) return { mode: 'plain' as const, index: -1, value: inputValue };
    const last = composerBlocks.length - 1;
    const b = composerBlocks[last];
    return b.type === 'text'
      ? { mode: 'block' as const, index: last, value: b.text }
      : { mode: 'virtual' as const, index: -1, value: '' };
  }, [composerBlocks, inputValue]);
  const bottomRef = useRef(bottom);
  bottomRef.current = bottom;

  /** 맨 아래 textarea의 새 값 → 전체 본문 문자열 */
  const composeFromBottom = useCallback((v: string) => {
    const cur = inputValueRef.current;
    const b = bottomRef.current;
    if (b.mode === 'plain') return v;
    if (b.mode === 'block') return replaceBlockText(cur, b.index, v);
    return v ? cur + '\n' + v : cur; // virtual — 표 뒤에 첫 글자가 들어온 순간 실제 블록이 된다
  }, []);

  /** 텍스트 블록(위에 쌓인 것 포함)의 새 값 → 전체 본문 문자열 */
  const composeFromBlock = useCallback((blockIndex: number | null, v: string) => {
    if (blockIndex === null) return composeFromBottom(v);
    return replaceBlockText(inputValueRef.current, blockIndex, v);
  }, [composeFromBottom]);

  const showTableNotice = useCallback((message: string) => {
    if (tableNoticeTimerRef.current) clearTimeout(tableNoticeTimerRef.current);
    setTableNotice(message);
    tableNoticeTimerRef.current = setTimeout(() => setTableNotice(null), 4000);
  }, []);

  /**
   * 셀 편집 커밋.
   *
   * ⚠ 셀을 다 비우면 표 판정(R4·R5·R7)이 뒤집혀 격자가 편집 도중 사라지고 이웃 텍스트에 흡수된다.
   *   판정 함수는 붙여넣기와 공유하므로 손대지 않고, 여기서 커밋을 되돌려 막는다.
   */
  const commitTableRows = useCallback((blockIndex: number, rows: string[][]) => {
    if (!looksLikeTable(rows, rows[0].length === 1)) {
      showTableNotice('이 칸을 비우면 표가 풀립니다. 표를 없애려면 오른쪽 위 X를 누르세요.');
      return;
    }
    const next = replaceBlockText(inputValueRef.current, blockIndex, rowsToTsv(rows));
    setInputValue(next);
    emitTyping(next);
  }, [showTableNotice]);

  const removeTableBlock = useCallback((blockIndex: number) => {
    const next = removeBlock(inputValueRef.current, blockIndex);
    setInputValue(next);
    emitTyping(next);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, []);

  const { members: channelMembers, loading: membersLoading } = useChannelMembers(mentionEnabled ? channel.id : null);
  const { allUsers: affiliateUsers, usersByDepartment } = useAffiliateUserSearch({
    isActive: mentionSourceActive,
    currentUserId: currentUser.id,
  });

  const myChannelRole = useMemo(
    () => channelMembers.find(m => String(m.userId) === String(currentUser.id))?.role,
    [channelMembers, currentUser.id]
  );
  const canMentionAll = currentUser.isAdmin === true || myChannelRole === 'owner' || myChannelRole === 'admin';

  // 후보 병합·필터 (섹션 순서: 멤버 → 특수 → 부서 → 조직도 비멤버)
  const mentionCandidates = useMemo<MentionCandidate[]>(() => {
    if (mentionQuery === null || !mentionEnabled) return [];
    const q = mentionQuery.toLowerCase();
    const memberIdSet = new Set(channelMembers.map(m => String(m.userId)));
    const affiliateById = new Map(affiliateUsers.map(u => [String(u.id), u]));
    const list: MentionCandidate[] = [];

    // ① 채널 멤버 (부서는 조직도 조인으로 보강 — ChannelMember에는 depart가 없음)
    channelMembers
      .filter(m => String(m.userId) !== String(currentUser.id))
      .filter(m => !q || m.userName.toLowerCase().includes(q))
      .slice(0, 8)
      .forEach(m => list.push({
        kind: 'member',
        userId: String(m.userId),
        name: m.userName,
        depart: affiliateById.get(String(m.userId))?.depart,
        avatar: m.avatar,
      }));

    // ② 특수 멘션 (@all — 대화방 참여자 전원. 관리자/채널 owner·admin만 노출, 서버도 동일 기준으로 재검증)
    if (canMentionAll && (!q || 'all'.startsWith(q))) list.push({ kind: 'special', special: 'all' });

    // ③ 부서 (채널 멤버와 교집합이 있는 부서만 — 서버가 활성 멤버로 클램프)
    usersByDepartment
      .filter(([dept]) => dept !== '미배정' && (!q || dept.toLowerCase().includes(q)))
      .map(([dept, users]) => {
        const userIds = users.map(u => String(u.id));
        return { dept, userIds, memberCount: userIds.filter(id => memberIdSet.has(id)).length };
      })
      .filter(d => d.memberCount > 0)
      .slice(0, 3)
      .forEach(d => list.push({ kind: 'dept', name: d.dept, userIds: d.userIds, memberCount: d.memberCount }));

    // ④ 조직도 비멤버 (선택은 가능하되 알림은 가지 않음 — 서버 클램프 + 인라인 고지)
    if (q) {
      affiliateUsers
        .filter(u => !memberIdSet.has(String(u.id)) && u.username.toLowerCase().includes(q))
        .slice(0, 5)
        .forEach(u => list.push({
          kind: 'outsider',
          userId: String(u.id),
          name: u.username,
          depart: u.depart,
          avatar: u.avatar,
        }));
    }

    return list;
  }, [mentionQuery, mentionEnabled, channelMembers, affiliateUsers, usersByDepartment, canMentionAll, currentUser.id]);

  // 커서 앞 텍스트에서 @토큰 감지 → 팝업 open/close + 필터어 갱신
  const updateMentionQuery = useCallback((el: HTMLTextAreaElement) => {
    if (!mentionEnabled) return;
    const cursor = el.selectionStart ?? el.value.length;
    const before = el.value.slice(0, cursor);
    const match = before.match(/(?:^|\s)@([^\s@]*)$/);
    if (match) {
      setMentionQuery(prev => {
        if (prev === null) setMentionIndex(0);
        return match[1];
      });
      setMentionSourceActive(true);
    } else {
      setMentionQuery(null);
    }
  }, [mentionEnabled]);

  const closeMentionPopup = useCallback(() => setMentionQuery(null), []);

  // 후보 선택: @토큰을 '@표시명 '으로 치환 + 원장 기록
  const selectMentionCandidate = useCallback((c: MentionCandidate) => {
    // 포커스된 텍스트 블록에 넣는다. 표 셀에는 멘션 자동완성을 붙이지 않으므로 여기 오지 않는다.
    const focus = composerFocusRef.current;
    const el = focus && focus.kind === 'textarea' ? focus.el : textareaRef.current;
    const blockIndex = focus && focus.kind === 'textarea' ? focus.blockIndex : null;
    if (!el) return;
    const cursor = el.selectionStart ?? el.value.length;
    const before = el.value.slice(0, cursor);
    const match = before.match(/(?:^|\s)@([^\s@]*)$/);
    if (!match) { setMentionQuery(null); return; }

    const displayName = c.kind === 'special' ? c.special : c.name;
    const input: MentionInput =
      c.kind === 'special'
        ? { type: 'all' }
        : c.kind === 'dept'
          ? { type: 'dept', name: c.name, userIds: c.userIds }
          : { type: 'user', userId: c.userId, name: c.name };

    const tokenStart = cursor - match[1].length - 1; // '@' 포함 시작 위치
    // el.value는 '그 블록의 값'이다(표가 없으면 본문 전체와 같다) — 스플라이스해 전체 본문으로 되돌린다
    const newBlockValue = el.value.slice(0, tokenStart) + '@' + displayName + ' ' + el.value.slice(cursor);
    setInputValue(composeFromBlock(blockIndex, newBlockValue));
    pendingMentionsRef.current.set(displayName, input);
    setMentionQuery(null);

    if (c.kind === 'outsider') {
      if (outsiderNoticeTimerRef.current) clearTimeout(outsiderNoticeTimerRef.current);
      setOutsiderNotice(`${c.name}님은 채널 멤버가 아니라 알림을 받지 않습니다.`);
      outsiderNoticeTimerRef.current = setTimeout(() => setOutsiderNotice(null), 4000);
    }

    // 커서를 삽입한 멘션 뒤로 이동 (setState 반영 이후)
    const caretPos = tokenStart + displayName.length + 2;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(caretPos, caretPos);
    });
  }, [composeFromBlock]);

  // submit 시 최종 텍스트를 재스캔 — 중간 편집으로 지워진 멘션은 자동 탈락.
  // 부서 멘션의 userIds가 비어 있으면(편집 재진입 케이스) 조직도에서 재충전
  const collectMentions = useCallback((text: string): MentionInput[] => {
    const result: MentionInput[] = [];
    pendingMentionsRef.current.forEach((input, name) => {
      if (!text.includes('@' + name)) return;
      if (input.type === 'dept' && input.userIds.length === 0) {
        const deptName = input.name;
        const grp = usersByDepartment.find(([d]) => d === deptName);
        result.push(grp ? { type: 'dept', name: deptName, userIds: grp[1].map(u => String(u.id)) } : input);
        return;
      }
      result.push(input);
    });
    return result;
  }, [usersByDepartment]);

  // 타이핑 표시 — 수신은 훅, 발신은 textarea onChange 스로틀.
  // throttleRef는 "마지막 startTyping 발신 시각"이자 0이면 미발신 상태 플래그
  const { typingUsers } = useTypingIndicator(channel.id, String(currentUser.id));
  const typingSentAtRef = useRef(0);
  const emitTyping = (value: string) => {
    // 초안 DM(channel.id === 0)은 서버에 없다 — 존재하지 않는 룸으로 타이핑 신호를 보내지 않는다
    if (isReadOnly || !channel.id) return;
    if (!value) {
      if (typingSentAtRef.current) {
        socketService.stopTyping(channel.id);
        typingSentAtRef.current = 0;
      }
      return;
    }
    const now = Date.now();
    if (now - typingSentAtRef.current > 1500) {
      typingSentAtRef.current = now;
      socketService.startTyping(channel.id);
    }
  };
  // 채널 전환/언마운트 시 이전 채널에 stop 통지 (cleanup이 이전 channel.id를 캡처)
  useEffect(() => () => {
    if (typingSentAtRef.current) {
      socketService.stopTyping(channel.id);
      typingSentAtRef.current = 0;
    }
  }, [channel.id]);

  const [isAtBottom, setIsAtBottom] = useState(true);

  const isInitialScroll = useRef(true);
  const skipAutoScrollRef = useRef(false);
  const prevScrollHeightRef = useRef(0);
  // 날짜 점프 진행 중 표시. 이 동안에는 자동 하단 스크롤/ResizeObserver 고정을 모두 막는다
  // (skipAutoScrollRef를 재사용하면 prevScrollHeight 기반 앵커링이 함께 돌아 점프 위치가 어긋난다)
  const jumpPendingRef = useRef(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesContentRef = useRef<HTMLDivElement>(null);
  // ResizeObserver 콜백에서 최신 isAtBottom을 읽기 위한 미러 ref
  const isAtBottomRef = useRef(true);
  useEffect(() => {
    isAtBottomRef.current = isAtBottom;
  }, [isAtBottom]);

  const scrollToBottom = (smooth?: boolean) => {
    const container = messagesContainerRef.current;
    if (container) {
      if (smooth) {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      } else {
        container.scrollTop = container.scrollHeight;
      }
    }
  };

  // 내가 보낸 메시지는 스크롤 위치와 무관하게 보여야 한다. 에코(message:new)가 [messages] effect의
  // 하단 고정을 타도록 미리 하단으로 옮기고 '하단에 있음'을 ref까지 즉시 세운다(state 반영은 비동기라
  // 에코가 먼저 도착하면 놓친다). 날짜·답장 점프 직후 2초 가드도 풀어 준다 — 보내는 것이 곧 최신으로 가겠다는 뜻.
  const pinToBottom = () => {
    jumpPendingRef.current = false;
    isAtBottomRef.current = true;
    setIsAtBottom(true);
    scrollToBottom();
  };

  // 매 스크롤 프레임마다 layout 읽기(scrollHeight 등)를 피하기 위한 rAF 스로틀
  const scrollRafRef = useRef(0);
  const handleScroll = () => {
    if (scrollRafRef.current) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = 0;
      const container = messagesContainerRef.current;
      if (!container) return;
      const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
      setIsAtBottom(atBottom);
    });
  };

  // 채널 변경 시 즉시 스크롤
  useEffect(() => {
    isInitialScroll.current = true;
    isAtBottomRef.current = true;
    setIsAtBottom(true);
    setJumpDate('');
    scrollToBottom();
  }, [channel.id]);

  // 메시지 변경 시 하단 고정 (이전 메시지 로드 시에는 스크롤 위치 유지)
  // 이미지/폰트 로드 등 늦은 레이아웃 변화는 아래 ResizeObserver가 처리
  useEffect(() => {
    // 날짜 점프로 메시지 창이 통째로 교체된 경우 — 앵커 스크롤이 담당하므로 여기선 손대지 않는다
    if (jumpPendingRef.current) return;
    if (skipAutoScrollRef.current) {
      skipAutoScrollRef.current = false;
      const container = messagesContainerRef.current;
      if (container && prevScrollHeightRef.current > 0) {
        container.scrollTop = container.scrollHeight - prevScrollHeightRef.current;
        prevScrollHeightRef.current = 0;
      }
      return;
    }
    if (isInitialScroll.current) {
      isInitialScroll.current = false;
      scrollToBottom();
      return;
    }
    // 스크롤이 하단에 있을 때만 자동 스크롤 (컨텍스트 메뉴 열림 중에는 리스트 정지)
    if (isAtBottomRef.current && !msgMenuOpenRef.current) {
      scrollToBottom();
    }
  }, [messages]);

  // 콘텐츠 높이 변화(이미지/폰트 로드 등) 시 하단 고정 — 기존 고정 지연 타이머(50~1800ms) 대체
  useEffect(() => {
    const content = messagesContentRef.current;
    if (!content) return;
    const observer = new ResizeObserver(() => {
      if (!isAtBottomRef.current || skipAutoScrollRef.current || msgMenuOpenRef.current || jumpPendingRef.current) return;
      // RO 콜백 안에서 동기 scrollTop 대입 시 "ResizeObserver loop" 경고 → rAF로 지연
      requestAnimationFrame(() => {
        if (isAtBottomRef.current && !skipAutoScrollRef.current && !msgMenuOpenRef.current && !jumpPendingRef.current) scrollToBottom();
      });
    });
    observer.observe(content);
    // 키보드 개폐 등으로 스크롤 컨테이너 자체가 줄어들 때도 하단을 유지한다
    // (콘텐츠 높이는 그대로라 content 관찰만으로는 발화하지 않는다)
    if (messagesContainerRef.current) observer.observe(messagesContainerRef.current);
    return () => observer.disconnect();
  }, []);

  // Ctrl+F로 검색창 열기
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
      if (e.key === 'Escape' && isSearchOpen) {
        closeSearch();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen]);

  // 검색 실행
  useEffect(() => {
    if (!searchQuery.trim()) {
      // 이미 빈 상태면 동일 참조 반환으로 리렌더 회피 (메시지 수신마다 공회전 방지)
      setSearchResults(prev => (prev.length === 0 ? prev : []));
      setCurrentSearchIndex(0);
      setHighlightedMessageId(null);
      return;
    }

    const query = searchQuery.toLowerCase();
    const results = messages
      .filter(msg => !msg.deletedAt && msg.text.toLowerCase().includes(query))
      .map(msg => msg.id);

    setSearchResults(results);
    setCurrentSearchIndex(0);

    if (results.length > 0) {
      scrollToMessage(results[0]);
    } else {
      setHighlightedMessageId(null);
    }
  }, [searchQuery, messages]);

  const scrollToMessage = useCallback((messageId: number) => {
    setHighlightedMessageId(messageId);
    const element = messageRefs.current.get(messageId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  // 날짜 점프 앵커로 스크롤.
  // messages 교체 → DOM 커밋이 끝나야 messageRefs에 ref가 들어오므로 useLayoutEffect + rAF로 미룬다
  useLayoutEffect(() => {
    if (!jumpTarget) return;
    jumpPendingRef.current = true;
    const raf = requestAnimationFrame(() => {
      scrollToMessage(jumpTarget.id);
    });
    // 답장 인용 점프(jumpToMessage)와 동일하게 2초 후 하이라이트 해제
    const timer = setTimeout(() => {
      setHighlightedMessageId(null);
      jumpPendingRef.current = false;
    }, 2000);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      jumpPendingRef.current = false;
    };
  }, [jumpTarget]);

  const goToPrevResult = () => {
    if (searchResults.length === 0) return;
    const newIndex = currentSearchIndex === 0 ? searchResults.length - 1 : currentSearchIndex - 1;
    setCurrentSearchIndex(newIndex);
    scrollToMessage(searchResults[newIndex]);
  };

  const goToNextResult = () => {
    if (searchResults.length === 0) return;
    const newIndex = currentSearchIndex === searchResults.length - 1 ? 0 : currentSearchIndex + 1;
    setCurrentSearchIndex(newIndex);
    scrollToMessage(searchResults[newIndex]);
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    setHighlightedMessageId(null);
    setJumpDate('');
  };

  const jumpDateInputRef = useRef<HTMLInputElement>(null);
  const openDatePicker = () => {
    const el = jumpDateInputRef.current;
    if (!el) return;
    try {
      el.showPicker(); // showPicker는 display:none 요소에서 예외 — 숨김 input은 opacity-0으로 처리
    } catch {
      el.focus();
      el.click();
    }
  };

  // 멘션이 나를 대상으로 하는지 (강조색 구분용)
  // 'here'는 폐지된 레거시 타입 — 과거 메시지 렌더 호환을 위해 판정만 남긴다
  // 아래 렌더 체인(mentionTargetsMe → mentionifyPart → linkifyText → highlightText →
  // renderBubbleContent)은 전부 useCallback으로 묶는다. 하나라도 매 렌더 새로 만들어지면
  // 최종 renderBubbleContent 참조가 바뀌고, 그것을 prop으로 받는 MessageItem의 memo가
  // 통째로 무력화되어 입력창 타이핑 한 글자마다 메시지 100건이 전부 리렌더된다.
  const mentionTargetsMe = useCallback((m: MessageMention): boolean => {
    if (m.type === 'here' || m.type === 'all') return true;
    if (m.type === 'user') return String(m.targetId) === String(currentUser.id);
    if (m.type === 'dept') return !!currentUser.depart && m.targetId === currentUser.depart;
    return false;
  }, [currentUser.id, currentUser.depart]);

  // 일반 텍스트 파트에서 @표시명을 멘션 칩으로 치환 (message_mentions 조인 렌더 — D1)
  // mentions 배열이 없는 메시지(전달본·구 메시지)는 그대로 반환
  const mentionifyPart = useCallback((part: string, isOwn: boolean, mentions: MessageMention[], keyBase: number): React.ReactNode[] => {
    const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // 긴 이름 우선 매칭 (@김철수 vs @김철)
    const names = Array.from(new Set(mentions.map(m => '@' + m.name)))
      .sort((a, b) => b.length - a.length);
    if (names.length === 0) return [part];
    const pattern = new RegExp(`(${names.map(escapeRe).join('|')})`, 'g');
    return part.split(pattern).map((seg, i) => {
      const mention = seg.startsWith('@') ? mentions.find(m => '@' + m.name === seg) : undefined;
      if (!mention) return seg;
      const isMe = mentionTargetsMe(mention);
      return (
        <span
          key={`m${keyBase}-${i}`}
          className={`font-semibold rounded px-0.5 ${
            isOwn
              ? 'bg-white/70 text-brand-700'
              : isMe
                ? 'bg-amber-200/90 text-amber-900'
                : 'bg-brand-50 text-brand-700'
          }`}
        >
          {seg}
        </span>
      );
    });
  }, [mentionTargetsMe]);

  // URL을 클릭 가능한 링크로 변환 (+@멘션 칩 — 텍스트 최말단 처리기)
  // 분할 규칙은 src/utils/linkify.ts 하나뿐이다 — 미리보기 카드가 가리키는 주소와
  // 말풍선 안의 링크가 반드시 같아야 하므로(후행 문장부호 처리 포함) 정규식을 여기 두지 않는다.
  const linkifyText = useCallback((text: string, isOwn = false, mentions?: MessageMention[]): React.ReactNode[] => {
    return splitByUrl(text).map((seg, i) =>
      seg.type === 'url' ? (
        <a
          key={i}
          href={seg.value}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            // 데스크톱은 기본 브라우저로 — WebView 안에서 열리면 돌아올 방법이 없다
            if (isTauri()) {
              e.preventDefault();
              void openExternalUrl(seg.value);
            }
          }}
          className={`underline break-all ${isOwn ? 'text-brand-700 hover:text-brand-800' : 'text-brand-600 hover:text-brand-700'}`}
        >
          {seg.value}
        </a>
      ) : (
        mentions && mentions.length > 0 ? mentionifyPart(seg.value, isOwn, mentions, i) : seg.value
      )
    );
  }, [mentionifyPart]);

  // 검색어 하이라이트 함수
  const highlightText = useCallback((text: string, isOwn = false, mentions?: MessageMention[]) => {
    if (!searchQuery.trim()) return linkifyText(text, isOwn, mentions);

    const query = searchQuery.toLowerCase();
    const index = text.toLowerCase().indexOf(query);

    if (index === -1) return linkifyText(text, isOwn, mentions);

    const before = text.slice(0, index);
    const match = text.slice(index, index + searchQuery.length);
    const after = text.slice(index + searchQuery.length);

    return (
      <>
        {linkifyText(before, isOwn, mentions)}
        <mark className="bg-yellow-300 text-slate-800 rounded px-0.5">{match}</mark>
        {linkifyText(after, isOwn, mentions)}
      </>
    );
  }, [searchQuery, linkifyText]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 초안 DM은 전송 도중 실제 방으로 승격되며 키가 dm:{사번} → ch:{id}로 바뀐다.
    // 지워야 할 것은 '보낼 때 있던 방'의 초안이다.
    const keyAtSubmit = draftKeyRef.current;

    // 편집 모드: 첨부 업로드 경로를 타지 않고 수정 emit만. 반영은 서버의 message:edited 에코가 담당
    if (editingMsg) {
      const text = inputValue.trim();
      // ⚠ 비교 대상은 editingMsg.text가 아니라 '편집 진입 시 정규화된 본문'이다.
      //   원문과 비교하면 표 메시지를 열었다 그냥 저장만 해도 정규화 차이로 '(수정됨)'이 붙는다.
      if (text && text !== editBaselineRef.current.trim()) {
        socketService.editMessage(editingMsg.id, channel.id, text, collectMentions(text));
      }
      cancelEdit();
      emitTyping('');
      return;
    }

    const hasContent = inputValue.trim() || attachments.length > 0;

    if (hasContent && !isUploading && !isSending) {
      setIsSending(true);
      try {
        // 첨부파일이 있으면 서버에 업로드
        if (attachments.length > 0 && pendingFiles.size > 0) {
          setIsUploading(true);

          // File 객체 배열 생성
          const filesToUpload = Array.from(pendingFiles.values());

          // 서버에 업로드
          const uploadedFiles = await dbService.uploadFiles(filesToUpload, setUploadProgress);

          // 메시지 텍스트 (첨부파일만 있을 경우 기본 텍스트)
          const messageText = inputValue.trim() || '파일을 공유했습니다.';

          // 첨부파일 정보와 함께 메시지 전송
          const sent = await onSendMessage(messageText, uploadedFiles, replyTo?.id ?? null, collectMentions(messageText));
          // 초안 DM의 채널 생성 실패 등 — 입력·첨부를 그대로 두어 그대로 재시도할 수 있게 한다
          if (sent === false) return;
        } else {
          const text = inputValue.trim();
          const sent = await onSendMessage(text, undefined, replyTo?.id ?? null, collectMentions(text));
          if (sent === false) return;
        }

        pinToBottom();
        setInputValue('');
        setAttachments([]);
        setPendingFiles(new Map());
        setShowEmojiPicker(false);
        setReplyTo(null);
        pendingMentionsRef.current.clear();
        setMentionQuery(null);
        emitTyping('');
        // 보관 중이던 초안 삭제. 미러도 같이 비운다 — 초안 DM 승격으로 리렌더가 끼면
        // cleanup이 위 setState 반영 전의 미러를 읽어 방금 보낸 글을 되살린다
        composerRef.current = emptyDraft();
        clearDraft(draftUserId, keyAtSubmit);
      } catch (error) {
        console.error('Upload failed:', error);
        const message = error instanceof Error && error.message
          ? error.message
          : '파일을 업로드하지 못했습니다.';
        showToast({ type: 'error', message });
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
        // 메시지 전송 후 잠시 딜레이를 주어 중복 방지 (선택 사항)
        setTimeout(() => setIsSending(false), 500);
      }
    }
  };

  // ----------------------------------------
  // 예약 전송 — 진입은 전송 버튼 롱프레스/우클릭 + 첨부 "+" 드롭다운 (발견성 보조)
  // ----------------------------------------
  const scheduleEnabled = !!onScheduleMessage && !isReadOnly;
  // 이 방의 내 예약만 (App은 전 채널 목록을 내려준다)
  const channelScheduled = useMemo(
    () => (scheduledMessages || []).filter(sm => Number(sm.channelId) === Number(channel.id)),
    [scheduledMessages, channel.id]
  );
  const hasFailedScheduled = channelScheduled.some(sm => !!sm.failedAt);

  /**
   * 예약 창 열기. fromComposer(전송 버튼 메뉴·클립 메뉴)면 입력창의 글·첨부·멘션을 넘겨받는다 —
   * 예약에 성공하면 그때 입력창을 비우고, 그냥 닫으면 입력창은 그대로다. 칩으로 열면 빈 폼.
   */
  /**
   * 대화방에서 일정 만들기(메시지 메뉴 '일정 만들기' · 클립 메뉴 '일정 공유') — 편집기는 App 루트 CalendarModalsHost가
   * 연다(calendarService 요청 스토어라 새 prop이 없다). 참석자는 이 방의 사람으로 채우고(1:1은 상대, 그룹·비공개는
   * 멤버 — 공개 채널은 수백 명이라 채우지 않는다), '이 대화방에 공유'를 켠 채 연다. 편집기가 같은 계열사 명부에
   * 없는 사람은 걸러 낸다(서버는 같은 계열사 재직자만 받는다).
   */
  const openCalendarEditorHere = (msg?: Message) => {
    if (!channel.id) return; // 초안 DM(id 0)은 아직 방이 없다 — 공유할 곳이 없다
    let attendees: Array<{ userId: string; name: string }> = [];
    if (channel.type === 'dm' && channel.dmPartnerId) {
      attendees = [{ userId: String(channel.dmPartnerId), name: channel.name || '' }];
    } else if (channel.type === 'group' || channel.type === 'private') {
      attendees = channelMembers
        .filter(m => String(m.userId) !== String(currentUser.id))
        .slice(0, 50)
        .map(m => ({ userId: String(m.userId), name: m.userName }));
    }
    const canShareHere = channel.type !== 'public' || currentUser.isAdmin === true;
    const roomName = channel.type === 'self' ? '나와의 채팅' : (channel.name || '대화방');
    const text = msg ? textForClipboard(msg.text || '').trim() : '';
    requestOpenEditor({
      title: text ? text.split('\n')[0].slice(0, 50) : '',
      description: text ? `${text.slice(0, 1900)}\n\n— ${roomName} 대화방에서` : '',
      attendees,
      shareChannel: canShareHere ? { id: channel.id, name: roomName } : null,
    });
  };

  const openScheduleModal = (fromComposer: boolean) => {
    if (!scheduleEnabled || editingMsg) return;
    const hasComposerContent = inputValue.trim().length > 0 || pendingFiles.size > 0;
    if (!fromComposer || !hasComposerContent) {
      setScheduleModal({ seed: null });
      return;
    }
    // 업로드 대상의 원장은 pendingFiles다(사진 미리보기는 FileReader가 끝나야 attachments에 들어온다)
    const thumbs = new Map(attachments.map(a => [a.id, a.thumbnail]));
    setScheduleModal({
      seed: {
        text: inputValue,
        mentions: collectMentions(inputValue),
        files: Array.from(pendingFiles.entries()).map(([id, file]) => ({ file, thumbnail: thumbs.get(id) })),
        // ⚠ 지금 잡는다 — 초안 DM은 예약 도중 실제 방으로 승격되며 키가 dm:{사번} → ch:{id}로 바뀐다
        draftKey: draftKeyRef.current,
      },
    });
  };

  // 전송 버튼 롱프레스(터치) — 채널 목록 롱프레스와 동일 관례. 발화 시 touchend preventDefault가
  // compat click(=submit)을 막아 길게 눌렀는데 전송되는 사고가 없다
  // ⚠ 터치 clientX/Y를 쓰지 않는다 — iOS 키보드 패닝(vv.offsetTop>0) 중에는 터치 좌표의
  // 좌표계가 body 포털 fixed(레이아웃 뷰포트)와 어긋나 메뉴가 헤더 쪽에 뜨는 사례가 있다.
  // 버튼 gBCR은 항상 fixed와 같은 좌표계라 키보드 유무와 무관하게 버튼 곁에 뜬다(클램프는 ContextMenu가 한다)
  const sendButtonRef = useRef<HTMLButtonElement>(null);
  const { handlers: sendLongPressHandlers } = useLongPress((pos) => {
    if (!scheduleEnabled || editingMsg) return;
    const rect = sendButtonRef.current?.getBoundingClientRect();
    setSendMenu(rect ? { x: rect.right, y: rect.top, via: 'touch' } : { x: pos.x, y: pos.y, via: 'touch' });
  });

  // 예약 등록 — 입력창에서 넘겨받은 내용이었다면 전송 성공과 같은 계약으로 입력창을 비운다
  // (실패면 그대로 두어 재시도). 모달은 닫지 않는다(폼만 비우고 목록에 새 예약이 뜬다)
  const handleScheduleCreate = async (body: ScheduleBody, fromSeed: boolean): Promise<boolean> => {
    if (!onScheduleMessage) return false;
    const draftKeyAtOpen = scheduleModal?.seed?.draftKey ?? draftKeyRef.current;
    const ok = await onScheduleMessage(body);
    if (!ok) return false;
    if (fromSeed) {
      setInputValue('');
      setAttachments([]);
      setPendingFiles(new Map());
      setShowEmojiPicker(false);
      setReplyTo(null);
      pendingMentionsRef.current.clear();
      setMentionQuery(null);
      emitTyping('');
      // 미러도 같이 비운다 — 초안 DM 승격으로 리렌더가 끼면 cleanup이 setState 반영 전의 미러를 읽어
      // 방금 예약한 글을 옛 키로 되살린다 (handleSubmit의 keyAtSubmit과 같은 이유)
      composerRef.current = emptyDraft();
      clearDraft(draftUserId, draftKeyAtOpen);
    }
    return true;
  };

  const handleScheduleUpdate = async (id: string, body: ScheduleBody): Promise<boolean> => {
    if (!onUpdateScheduledMessage) return false;
    return onUpdateScheduledMessage(id, body);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // 멘션 팝업 키 조작 — 반드시 최상단 (아래 IME 가드는 Enter 분기 이후에만 적용되는 구조).
    // 단 한글 조합 중에는 팝업 키도 무시해 조합 확정 Enter가 후보를 오선택하지 않게 한다
    if (mentionQuery !== null && mentionCandidates.length > 0) {
      if (!(e.nativeEvent.isComposing || e.keyCode === 229)) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setMentionIndex(i => Math.min(i + 1, mentionCandidates.length - 1));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setMentionIndex(i => Math.max(i - 1, 0));
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          selectMentionCandidate(mentionCandidates[Math.min(mentionIndex, mentionCandidates.length - 1)]);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          closeMentionPopup();
          return;
        }
      }
    }
    if (e.key === 'Escape' && editingMsg) {
      e.preventDefault();
      cancelEdit();
      return;
    }
    if (e.key !== 'Enter') return;
    // IME 조합 중 Enter(한글 마지막 음절 확정)는 전송으로 이어지면 안 됨 (keyCode 229는 구형 WebView 호환)
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    if (IS_COARSE_POINTER) return;
    if (!e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // 입력 내용에 따라 textarea 높이 자동 조절 (전송 후 inputValue가 비워지면 최소 높이로 복원)
  useEffect(() => {
    const fit = (ta: HTMLTextAreaElement | null) => {
      if (!ta) return;
      ta.style.height = 'auto';                       // 먼저 초기화해야 줄어들 때도 정확히 측정됨
      ta.style.height = `${ta.scrollHeight}px`;       // 내용 높이에 맞춤 (CSS max-h-32가 128px로 상한)
    };
    fit(textareaRef.current);                          // 맨 아래 textarea (표가 없으면 이것뿐)
    textareaRefs.current.forEach(fit);                 // 표 위에 쌓인 텍스트 블록들
  }, [inputValue]);

  // window.confirm은 Tauri WebView2에서 동작하지 않으므로 자체 확인 모달 사용
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);

  const handleDelete = (msgId: number) => {
    setDeleteTargetId(msgId);
  };

  const confirmDelete = async () => {
    if (deleteTargetId === null) return;
    const msgId = deleteTargetId;
    setDeleteTargetId(null);
    try {
      await dbService.deleteMessage(msgId);
      // 즉각 반영: App의 messages state에 deletedAt 마킹
      onDeleteMessage?.(msgId);
    } catch (error) {
      showToast({ type: 'error', message: '메시지를 삭제하지 못했습니다.' });
    }
  };

  // 이모지 피커 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  // 이미지 메뉴 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (imageMenuRef.current && !imageMenuRef.current.contains(event.target as Node)) {
        setShowImageMenu(false);
      }
    };
    if (showImageMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showImageMenu]);

  // 리액션 피커 외부 클릭 감지
  useEffect(() => {
    if (reactionPickerMsgId === null) return;
    const handleClickOutside = () => setReactionPickerMsgId(null);
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [reactionPickerMsgId]);

  // 드래그앤드롭 핸들러
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    dragCounterRef.current = 0;
    if (isReadOnly) return;
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      addFiles(files);
    }
  };

  // 화면 캡처 시작
  const startScreenCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: 'always' } as any
      });

      const video = document.createElement('video');
      video.srcObject = stream;
      await video.play();

      // 비디오 프레임이 렌더링될 때까지 대기
      await new Promise(resolve => requestAnimationFrame(resolve));

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(video, 0, 0);

      // 스트림 중지
      stream.getTracks().forEach(track => track.stop());
      video.remove();

      const screenshotDataUrl = canvas.toDataURL('image/png');
      setCapturedScreenshot(screenshotDataUrl);
      setIsCapturing(true);
    } catch (err) {
      // 선택 창에서 취소한 것은 오류가 아니다
      const name = (err as { name?: string } | null)?.name;
      if (name === 'NotAllowedError' || name === 'AbortError') return;
      console.error('화면 캡처 실패:', err);
      showToast({ type: 'error', message: '화면을 캡처하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    }
  };

  // 이모지 선택 핸들러
  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const emoji = emojiData.emoji;
    const focus = composerFocusRef.current;

    // 표 셀에 커서가 있으면 그 셀에 넣는다
    if (focus && focus.kind === 'cell') {
      const block = composerBlocks?.[focus.blockIndex];
      if (block && block.type === 'table') {
        const el = focus.el;
        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? start;
        const merged = el.value.slice(0, start) + emoji + el.value.slice(end);
        commitTableRows(focus.blockIndex, setCell(block.rows, focus.r, focus.c, merged));
        setTimeout(() => {
          el.focus();
          el.setSelectionRange(start + emoji.length, start + emoji.length);
        }, 0);
        return;
      }
    }

    const textarea = focus && focus.kind === 'textarea' ? focus.el : textareaRef.current;
    const blockIndex = focus && focus.kind === 'textarea' ? focus.blockIndex : null;

    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      // textarea.value는 '그 블록의 값'이다 — 표가 없으면 본문 전체와 같다
      const newBlockValue = textarea.value.slice(0, start) + emoji + textarea.value.slice(end);
      setInputValue(composeFromBlock(blockIndex, newBlockValue));

      // 커서 위치 조정
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + emoji.length, start + emoji.length);
      }, 0);
    } else {
      setInputValue(prev => prev + emoji);
    }
  };

  // 첨부 수를 ref에 동기화 (addFiles가 deps 없이 읽는다)
  useEffect(() => {
    attachmentCountRef.current = attachments.length;
  }, [attachments.length]);

  // 파일 추가 공통 헬퍼 (드래그앤드롭, 파일선택, 붙여넣기, Tauri 네이티브 드롭 공용)
  //
  // 관리자 콘솔에서 정한 차단 확장자·용량·개수 한도를 여기서 사전 차단한다. 서버(multer)도 같은 값으로
  // 2차 검증하므로 이 검사는 우회 방지가 아니라 "네트워크로 다 올린 뒤에야 거부당하는" 경험을 없애기
  // 위한 것이다. 판정(차단 확장자 → 용량 → 개수)과 안내 문구는 예약 모달과 공유하는
  // screenAttachmentFiles 하나이고, 정책값은 모듈 스토어의 동기 getter라 deps를 비운다.
  const addFiles = useCallback((files: File[]) => {
    const { accepted: withinCount, messages } = screenAttachmentFiles(files, attachmentCountRef.current);

    withinCount.forEach(file => {
      const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const isImage = file.type.startsWith('image/');

      if (isImage) {
        // 미리보기용 로컬 URL 생성 (이미지만)
        const reader = new FileReader();
        reader.onload = (event) => {
          const localUrl = event.target?.result as string;
          setAttachments(prev => [...prev, {
            id,
            name: file.name,
            size: file.size,
            type: file.type,
            url: localUrl,
            thumbnail: localUrl,
          }]);
        };
        reader.readAsDataURL(file);
      } else {
        // 비이미지는 data URL을 만들지 않는다. 미리보기는 thumbnail만 쓰고(없으면 확장자 아이콘),
        // 전송은 pendingFiles의 원본 File을 올린 뒤 서버가 준 URL을 쓴다 — 즉 이 data URL은 어디서도
        // 읽히지 않으면서 base64로 원본의 1.37배를 메모리에 얹는다.
        setAttachments(prev => [...prev, {
          id,
          name: file.name,
          size: file.size,
          type: file.type,
          url: '',
        }]);
      }

      // 업로드용 File 객체 저장
      setPendingFiles(prev => new Map(prev).set(id, file));
    });

    // 안내는 파일당이 아니라 집계해서 (8개가 초과하면 토스트가 8개 쌓인다)
    messages.forEach(message => showToast({ type: 'error', message }));
  }, [showToast]);

  // 붙여넣은 본문을 커서 위치에 넣는다. 표는 줄 시작에서 시작하고 줄 끝에서 끝나야 렌더 판정에
  // 걸리므로, 본문이 표로 시작·끝나면 앞뒤 개행을 보정한다. DOM 실값을 읽는 방식은
  // selectMentionCandidate와 같다.
  const insertComposerText = (
    text: string,
    startsWithTable: boolean,
    endsWithTable: boolean,
    el: HTMLTextAreaElement | null,
    blockIndex: number | null,
  ) => {
    // el.value는 '붙여넣기가 일어난 블록의 값'이다(표가 없으면 본문 전체와 같다)
    const base = el ? el.value : inputValueRef.current;
    const start = el ? (el.selectionStart ?? base.length) : base.length;
    const end = el ? (el.selectionEnd ?? start) : start;
    const before = base.slice(0, start);
    const after = base.slice(end);
    const lead = startsWithTable && before && !before.endsWith('\n') ? '\n' : '';
    // ⚠ 표로 끝나면 뒤 개행은 조건부가 아니라 항상 넣는다 — 표 뒤에 이어 쓸 텍스트 블록이 없으면
    //   컴포저 맨 아래 입력칸이 '가상' 상태가 되어 커서를 둘 실체가 없다.
    const trail = endsWithTable && !after.startsWith('\n') ? '\n' : '';
    const insert = lead + text + trail;
    const next = normalizeMessageText(composeFromBlock(blockIndex, before + insert + after));

    setInputValue(next);
    emitTyping(next); // ⚠ 멘션 삽입 경로가 빠뜨린 호출 — 여기서는 타이핑 인디케이터를 맞춘다
    // 붙여넣기로 블록이 새로 쪼개지면 textarea가 리마운트되어 포커스를 잃는다. 맨 아래로 되돌린다.
    requestAnimationFrame(() => {
      const tail = textareaRef.current;
      if (!tail) return;
      tail.focus();
      const caret = tail.value.length;
      tail.setSelectionRange(caret, caret);
      updateMentionQuery(tail); // 커서가 @토큰 밖으로 나갔으니 멘션 팝업 정리
    });
  };

  // 표만 붙여넣은 경우. 표 앞뒤는 언제나 줄이 나뉘어야 한다.
  const insertTableText = (table: ClipboardTable, el: HTMLTextAreaElement | null, blockIndex: number | null) => {
    insertComposerText(rowsToTsv(table.rows), true, true, el, blockIndex);

    // 안내는 붙여넣기 1회당 한 문장으로 집계한다 (addFiles의 토스트 정책과 같다)
    if (table.truncated) {
      const parts: string[] = [];
      if (table.truncated.rows > 0) parts.push(`${table.truncated.rows}행`);
      if (table.truncated.cols > 0) parts.push(`${table.truncated.cols}열`);
      showToast({
        type: 'info',
        message: `표는 한 번에 ${TABLE_MAX_ROWS}행 ${TABLE_MAX_COLS}열까지 붙여넣을 수 있어 나머지 ${parts.join(' ')}을 제외했습니다.`,
      });
    }
  };

  // 클립보드 붙여넣기 — 판정 '순서'가 곧 정책이다.
  //  1) 텍스트가 함께 실려 있으면 텍스트가 이긴다. 엑셀 셀 복사는 비트맵을 같이 싣기 때문에
  //     이미지 분기를 먼저 두면 표가 통째로 이미지가 된다(이 기능 이전의 실제 동작).
  //  2) 그 텍스트가 격자면 탭 구분 평문으로 정규화해 커서 위치에 넣는다.
  //     ⚠ 표 밖에도 글이 있으면(웹 페이지·AI 답변 복사) 표만 뽑지 않고 본문 전체를 넣는다 —
  //       표만 넣으면 아래 preventDefault 때문에 나머지 글이 조용히 사라진다.
  //  3) 격자가 아니면 아무것도 하지 않고 브라우저 기본 붙여넣기에 맡긴다(preventDefault 없음).
  //  4) 텍스트가 아예 없을 때만(캡처 도구·이미지 단독 복사) 이미지를 첨부한다.
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>, blockIndex: number | null) => {
    if (isReadOnly) return;
    const dt = e.clipboardData;
    if (!dt) return;

    const plain = dt.getData('text/plain') || '';
    if (plain.trim().length > 0) {
      const pasted = extractClipboardPaste(dt.getData('text/html') || '', plain);
      if (!pasted) return; // 일반 텍스트 → 브라우저 기본 동작
      e.preventDefault();
      if (pasted.kind === 'table') {
        insertTableText(pasted.table, e.currentTarget, blockIndex);
      } else {
        insertComposerText(pasted.text, pasted.startsWithTable, pasted.endsWithTable, e.currentTarget, blockIndex);
      }
      return;
    }

    // 클립보드 캡처 이미지 (무의미한 'image.png' 이름은 새로 짓는다 — 예약 모달과 공용)
    const imageFiles = pastedImageFiles(dt);
    if (imageFiles.length > 0) {
      e.preventDefault();
      addFiles(imageFiles);
    }
  };

  /** 블록 인덱스로 텍스트 입력칸에 포커스. 마지막 텍스트 블록은 맨 아래 textarea가 담당한다 */
  const focusTextBlock = (index: number) => {
    if (index < 0) {
      // 표가 맨 위라 위에 쓸 자리가 없다 — 선행 텍스트 슬롯을 만들고 거기로 간다
      setInputValue('\n' + inputValueRef.current);
      requestAnimationFrame(() => textareaRefs.current.get(0)?.focus());
      return;
    }
    const above = textareaRefs.current.get(index);
    if (above) {
      above.focus();
      return;
    }
    textareaRef.current?.focus();
  };

  // 액션 행 위에 쌓이는 컴포저 블록. 마지막 텍스트 블록은 아래 textarea가 담당하므로 뺀다.
  // 표가 없으면 composerBlocks가 null이라 이 값도 null이고 예전 DOM이 그대로 유지된다.
  const composerAbove = composerBlocks
    ? composerBlocks
        .slice(0, bottom.mode === 'block' ? composerBlocks.length - 1 : composerBlocks.length)
        .map((b, i) =>
          b.type === 'table' ? (
            <ComposerTableGrid
              key={i}
              rows={b.rows}
              hasHeader={b.hasHeader}
              align={b.align}
              darkMode={darkMode}
              onChange={(rows) => commitTableRows(i, rows)}
              onRemove={() => removeTableBlock(i)}
              onLeaveUp={() => focusTextBlock(i - 1)}
              onLeaveDown={() => focusTextBlock(i + 1)}
              onCellFocus={(r, c, el) => {
                composerFocusRef.current = { kind: 'cell', blockIndex: i, r, c, el };
              }}
            />
          ) : (
            <textarea
              key={i}
              ref={(el) => {
                if (el) textareaRefs.current.set(i, el);
                else textareaRefs.current.delete(i);
              }}
              value={b.text}
              onFocus={(e) => {
                composerFocusRef.current = { kind: 'textarea', blockIndex: i, el: e.currentTarget };
              }}
              onChange={(e) => {
                const next = composeFromBlock(i, e.target.value);
                setInputValue(next);
                emitTyping(next);
                updateMentionQuery(e.target);
              }}
              onKeyDown={handleKeyDown}
              onCompositionEnd={(e) => updateMentionQuery(e.currentTarget)}
              onClick={(e) => updateMentionQuery(e.currentTarget)}
              onKeyUp={(e) => {
                if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) updateMentionQuery(e.currentTarget);
              }}
              onPaste={(e) => handlePaste(e, i)}
              className={`w-full bg-transparent border-none focus:outline-none focus:ring-0 resize-none max-h-32 py-1 px-1.5 text-base md:text-sm ${darkMode ? 'text-gray-100 placeholder-gray-500' : 'text-slate-700 placeholder-slate-400'}`}
              rows={1}
            />
          )
        )
    : null;

  // Tauri 네이티브 드래그앤드롭 이벤트 리스너
  useEffect(() => {
    if (!isTauri()) return;

    let unlisten: (() => void) | undefined;
    let mounted = true;

    (async () => {
      try {
        const { getCurrentWebviewWindow } = await import('@tauri-apps/api/webviewWindow');
        const { invoke } = await import('@tauri-apps/api/core');
        const appWindow = getCurrentWebviewWindow();

        const cb = await appWindow.onDragDropEvent(async (event) => {
          if (!mounted) return;
          if (event.payload.type === 'enter') {
            // 예약 창이 떠 있으면 파일은 그 창으로 간다 — 뒤에 가려진 채팅창에 드롭 오버레이를 띄우지 않는다
            if (!scheduleDropSinkRef.current) setIsDragOver(true);
          } else if (event.payload.type === 'leave') {
            setIsDragOver(false);
          } else if (event.payload.type === 'drop') {
            setIsDragOver(false);
            if (channel.type === 'public') return;

            const paths: string[] = (event.payload as any).paths || [];
            const files: File[] = [];
            const oversized: string[] = [];
            const failed: string[] = [];
            const blocked: string[] = [];
            const { maxFileSizeMB } = getUploadPolicy();
            const maxBytes = maxFileSizeMB * 1024 * 1024;

            for (const filePath of paths) {
              const filename = filePath.split(/[\\/]/).pop() || 'file';
              // 차단 확장자는 읽기 전에 걸러낸다 — 용량 사전 검사와 같은 이유(읽는 순간 메모리가 뜬다)
              if (isBlockedFileName(filename)) {
                blocked.push(filename);
                continue;
              }
              try {
                // 읽기 전에 크기만 물어본다. 초과 파일을 통째로 읽으면 Rust 버퍼 + IPC 복사본으로
                // 메모리가 먼저 튄다 — addFiles의 사전 차단은 이미 늦은 시점이다.
                // 구버전 셸에는 이 명령이 없다 → 사전 검사를 건너뛰고 addFiles가 거른다.
                try {
                  const size = await invoke<number>('stat_dropped_file', { path: filePath });
                  if (typeof size === 'number' && size > maxBytes) {
                    oversized.push(filename);
                    continue;
                  }
                } catch {
                  /* 사전 검사 불가 — 아래에서 읽고 addFiles가 처리한다 */
                }

                const data: ArrayBuffer = await invoke('read_dropped_file', { path: filePath });
                files.push(new File([data], filename, { type: getMimeType(filename) }));
              } catch (err) {
                console.error('드롭 파일 읽기 실패:', filePath, err);
                failed.push(filename);
              }
            }

            if (files.length > 0) {
              // 예약 창이 떠 있으면 그 창의 첨부로 (안 그러면 보이지 않는 뒤쪽 입력창에 붙는다)
              (scheduleDropSinkRef.current ?? addFiles)(files);
            }
            // 예전에는 실패가 console.error로만 남아 사용자에게 아무 표시가 없었다.
            if (blocked.length > 0) {
              showToast({ type: 'error', message: blockedAttachmentMessage(blocked) });
            }
            if (oversized.length > 0) {
              showToast({
                type: 'error',
                message: oversized.length === 1
                  ? `${truncateFileName(oversized[0])}이 너무 커서 첨부하지 못했습니다. 최대 ${maxFileSizeMB}MB까지 첨부할 수 있습니다.`
                  : `파일 ${oversized.length}개가 너무 커서 첨부하지 못했습니다. 최대 ${maxFileSizeMB}MB까지 첨부할 수 있습니다.`,
              });
            }
            if (failed.length > 0) {
              showToast({
                type: 'error',
                message: `파일 ${failed.length}개를 읽지 못했습니다. 폴더이거나 접근할 수 없는 파일일 수 있습니다.`,
              });
            }
          }
        });
        unlisten = cb;
      } catch (err) {
        console.error('Tauri drag-drop 이벤트 등록 실패:', err);
      }
    })();

    return () => {
      mounted = false;
      unlisten?.();
    };
  }, [channel.type, addFiles, showToast]);

  // 파일 선택 핸들러
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    addFiles(Array.from(files));
    // input 초기화 (같은 파일 다시 선택 가능하도록)
    e.target.value = '';
  };

  // 첨부파일 제거
  const removeAttachment = (id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
    setPendingFiles(prev => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };

  // 파일 크기 포맷
  // 파일 다운로드 (일반 첨부파일용 — Tauri는 외부 브라우저, 웹은 다운로드)
  const downloadFile = useCallback(async (url: string, fileName: string) => {
    if (isTauri()) {
      // Tauri: 기본 브라우저에서 열기 (브라우저가 다운로드 처리)
      const { openUrl } = await import('@tauri-apps/plugin-opener');
      await openUrl(url);
    } else {
      // 웹: fetch → blob → 다운로드 트리거
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      } catch {
        window.open(url, '_blank');
      }
    }
  }, []);

  // 이미지 라이트박스 전용 저장 — Tauri는 "다른 이름으로 저장" 다이얼로그, 웹은 blob 다운로드
  const saveImageNative = useCallback(async (url: string, fileName: string) => {
    if (isTauri()) {
      try {
        const { save } = await import('@tauri-apps/plugin-dialog');
        const { writeFile } = await import('@tauri-apps/plugin-fs');
        const ext = (fileName.split('.').pop() || 'png').toLowerCase();
        const filePath = await save({
          defaultPath: fileName,
          filters: [
            { name: '이미지', extensions: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'] },
            { name: '모든 파일', extensions: [ext, '*'] },
          ],
        });
        if (!filePath) return; // 사용자 취소
        const response = await fetch(url);
        const buffer = new Uint8Array(await response.arrayBuffer());
        await writeFile(filePath, buffer);
      } catch (e) {
        console.error('이미지 저장 실패:', e);
      }
    } else {
      // 웹: 표준 다운로드 흐름
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      } catch {
        window.open(url, '_blank');
      }
    }
  }, []);

  // 같은 메시지의 이미지 첨부만 모아 라이트박스를 연다.
  const openLightboxFor = useCallback((messageAttachments: Attachment[], clickedId: string) => {
    const imageAttachments = messageAttachments.filter((a) => a.type.startsWith('image/'));
    const slides: LightboxSlide[] = imageAttachments.map((a) => {
      const url = resolveServerUrl(a.url);
      // 만료 항목도 슬라이드 배열에 남긴다 — 빼면 클릭 인덱스가 어긋난다.
      // ImageLightbox가 expired를 보고 이미지 대신 안내를 그리고 다운로드 버튼을 숨긴다.
      return {
        src: url,
        alt: a.name,
        downloadUrl: url,
        downloadFilename: a.name,
        expired: isAttachmentExpired(a.expiresAt),
      };
    });
    const index = Math.max(0, imageAttachments.findIndex((a) => a.id === clickedId));
    if (slides.length === 0) return;
    setLightbox({ open: true, slides, index });
  }, []);

  // 이미지 로드 실패 처리. 말풍선이 없어진 뒤로는 img를 그냥 숨기면 메시지가 화면에서 통째로
  // 사라지므로, 형제로 숨겨둔 대체 박스를 켠다. 리렌더 없이 DOM만 건드린다(기존 명령형 스타일 유지).
  const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    img.style.display = 'none';
    const fallback = img.nextElementSibling;
    if (fallback) {
      fallback.classList.remove('hidden');
      fallback.classList.add('flex');
    }
  }, []);

  const renderImageFallback = useCallback(() => (
    <div
      className={`hidden w-fit items-center gap-1.5 px-3 py-2 rounded-xl border text-xs ${
        darkMode ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-slate-100 border-slate-200 text-slate-400'
      }`}
    >
      <ImageOff size={14} className="shrink-0" />
      이미지를 불러오지 못했습니다
    </div>
  ), [darkMode]);

  /**
   * 보관 기간이 지난 이미지 자리. 원본이 삭제돼 src가 404이므로 img를 아예 그리지 않는다
   * (handleImageError 폴백은 "불러오지 못했습니다"라 원인을 오해하게 만든다).
   *
   * compact는 3열 썸네일 격자용 — 셀 폭이 100px 안팎이라 전체 문구가 뭉개진다.
   */
  const renderExpiredImage = useCallback((sizing: string, compact = false) => (
    <div
      title={EXPIRED_IMAGE_TEXT}
      className={`flex flex-col items-center justify-center gap-1 rounded-xl border text-center leading-tight ${
        compact ? 'px-1 py-1 text-[10px] rounded-lg' : 'px-3 py-2 text-xs'
      } ${sizing} ${
        darkMode ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-slate-100 border-slate-200 text-slate-400'
      }`}
    >
      <Clock size={compact ? 14 : 16} className="shrink-0" />
      {compact ? EXPIRED_IMAGE_SHORT_TEXT : EXPIRED_IMAGE_TEXT}
    </div>
  ), [darkMode]);

  // 첨부는 말풍선 '밖'에 그린다(카카오톡 방식) — 배경이 채팅 캔버스라 isOwn 기준 색 분기가 없다.
  // 블록 사이 간격은 MessageItem의 스택 래퍼(gap)가 담당하므로 여기서 여백을 두지 않는다.
  const renderAttachments = useCallback((messageAttachments: Attachment[]) => {
    if (!messageAttachments || messageAttachments.length === 0) return null;
    const resolveUrl = (att: Attachment) => resolveServerUrl(att.url);

    const imageAttachments = messageAttachments.filter((a) => a.type.startsWith('image/'));
    const fileAttachments = messageAttachments.filter((a) => !a.type.startsWith('image/'));

    const imageCount = imageAttachments.length;
    const MAX_VISIBLE = 9;
    const visibleImages = imageCount > MAX_VISIBLE ? imageAttachments.slice(0, MAX_VISIBLE) : imageAttachments;
    const remaining = imageCount > MAX_VISIBLE ? imageCount - MAX_VISIBLE : 0;

    let gridContainerClass = '';
    if (imageCount === 2 || imageCount === 4) {
      gridContainerClass = 'grid grid-cols-2 gap-1 max-w-[300px]';
    } else if (imageCount >= 3) {
      gridContainerClass = 'grid grid-cols-3 gap-1 max-w-[300px]';
    }

    const cellClass = 'aspect-square w-full object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity';

    const renderImageBlock = () => {
      if (imageCount === 0) return null;

      if (imageCount === 1) {
        const att = imageAttachments[0];
        if (isAttachmentExpired(att.expiresAt)) {
          return <div key={att.id}>{renderExpiredImage('w-[220px] h-[110px]')}</div>;
        }
        // 말풍선이 없으므로 고정 높이(object-contain)를 쓰면 빈 여백만큼 정렬이 어긋난다 → 원본 비율 유지
        return (
          <div key={att.id} className="relative">
            <img
              src={resolveUrl(att)}
              alt={att.name}
              loading="lazy"
              decoding="async"
              data-attachment-id={att.id}
              className="max-w-full sm:max-w-[300px] max-h-[280px] w-auto h-auto rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => openLightboxFor(messageAttachments, att.id)}
              onError={handleImageError}
            />
            {renderImageFallback()}
          </div>
        );
      }

      return (
        <div className={gridContainerClass}>
          {visibleImages.map((att, idx) => {
            const isLastWithOverflow = remaining > 0 && idx === MAX_VISIBLE - 1;
            const expired = isAttachmentExpired(att.expiresAt);
            return (
              <div key={att.id} className="relative">
                {expired ? renderExpiredImage('aspect-square w-full', true) : (
                  <>
                    <img
                      src={resolveUrl(att)}
                      alt={att.name}
                      loading="lazy"
                      decoding="async"
                      data-attachment-id={att.id}
                      className={cellClass}
                      onClick={() => openLightboxFor(messageAttachments, att.id)}
                      onError={handleImageError}
                    />
                    {renderImageFallback()}
                  </>
                )}
                {isLastWithOverflow && (
                  <div
                    className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center text-white font-semibold text-lg cursor-pointer pointer-events-none"
                  >
                    +{remaining}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    };

    const renderFileBlock = () => {
      if (fileAttachments.length === 0) return null;
      // items-end 스택 안에서 폭이 내용까지 붕괴하지 않도록 고정폭을 준다
      return (
        <div className="w-[280px] max-w-full space-y-2">
          {fileAttachments.map((attachment) => {
            const fileUrl = resolveUrl(attachment);
            const expired = isAttachmentExpired(attachment.expiresAt);
            const expiryLabel = formatExpiryLabel(attachment.expiresAt);
            // 만료 임박·만료는 경고라 brand가 소유한다. 다크 표면(영상통화 사이드바)에서는 brand-400
            const warnColor = darkMode ? 'text-brand-400' : 'text-brand-700';
            const TypeIcon = iconForFileName(attachment.name);
            return (
              <button
                key={attachment.id}
                // disabled를 쓰면 onClick이 죽어 안내 토스트도 사라진다 → 활성 상태로 두고 aria로 알린다
                aria-disabled={expired}
                onClick={() => (expired
                  ? showToast({ type: 'error', message: EXPIRED_DOWNLOAD_TOAST })
                  : downloadFile(fileUrl, attachment.name))}
                className={`flex items-center gap-2 p-2 rounded-xl border shadow-sm transition-colors w-full text-left ${
                  expired
                    ? darkMode
                      ? 'bg-gray-800/60 border-gray-700 cursor-not-allowed'
                      : 'bg-slate-100 border-slate-200 cursor-not-allowed'
                    : darkMode
                      ? 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                      : 'bg-white border-slate-200 hover:bg-slate-50'
                }`}
              >
                <div className={`p-2 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-slate-100'}`}>
                  {expired
                    ? <Clock size={20} className={darkMode ? 'text-gray-500' : 'text-slate-400'} />
                    : <TypeIcon size={20} className={darkMode ? 'text-gray-300' : 'text-brand-600'} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium break-all ${
                    expired
                      ? (darkMode ? 'text-gray-500' : 'text-slate-400')
                      : (darkMode ? 'text-gray-200' : 'text-slate-700')
                  }`}>
                    {attachment.name}
                  </div>
                  <div className={`text-xs ${
                    expired || isExpiringSoon(attachment.expiresAt)
                      ? warnColor
                      : darkMode ? 'text-gray-400' : 'text-slate-400'
                  }`}>
                    {expired
                      ? EXPIRED_FILE_TEXT
                      : [formatFileSize(attachment.size), expiryLabel].filter(Boolean).join(' · ')}
                  </div>
                </div>
                {!expired && <Download size={16} className={darkMode ? 'text-gray-400' : 'text-slate-400'} />}
              </button>
            );
          })}
        </div>
      );
    };

    return (
      <div className="space-y-2">
        {renderImageBlock()}
        {renderFileBlock()}
      </div>
    );
  }, [darkMode, openLightboxFor, downloadFile, handleImageError, renderImageFallback, renderExpiredImage, showToast]);

  // 답장 인용 클릭 → 원본 메시지로 스크롤 이동 (검색 로직 재사용)
  const jumpToMessage = useCallback((messageId: number) => {
    scrollToMessage(messageId);
    setTimeout(() => setHighlightedMessageId(null), 2000);
  }, [scrollToMessage]);

  // 답장 원본 조회용 인덱스 (답장 메시지마다 O(n) find 방지)
  const messageById = useMemo(() => {
    const map = new Map<number, Message>();
    messages.forEach(m => map.set(m.id, m));
    return map;
  }, [messages]);

  // 답장 원본 미리보기 (인용) 렌더
  const renderReplyQuote = useCallback((msg: Message, isOwn: boolean) => {
    if (!msg.replyToId) return null;
    const original = messageById.get(msg.replyToId);
    const isDeletedOriginal = original?.deletedAt;
    const originalText = !original
      ? '원본 메시지'
      : isDeletedOriginal
        ? '삭제된 메시지입니다'
        : (original.text && original.text !== '파일을 공유했습니다.')
          ? original.text
          : '첨부파일';
    return (
      <button
        type="button"
        onClick={() => original && jumpToMessage(original.id)}
        className={`w-full text-left block space-y-0.5 mb-1.5 pl-2 pr-2 py-1 rounded border-l-2 transition-colors ${
          isOwn
            ? 'border-brand-600 bg-white/60 hover:bg-white/90'
            : darkMode
              ? 'border-brand-600 bg-gray-700/50 hover:bg-gray-700'
              : 'border-brand-600 bg-slate-100 hover:bg-slate-200'
        }`}
      >
        {/* truncate(=white-space:nowrap)를 쓰면 인용문 전체 길이가 말풍선의 min-content가 되어
            말풍선이 채팅 영역 밖으로 밀려난다. line-clamp는 줄바꿈을 허용하면서 1줄 말줄임을 유지한다 */}
        <span className={`line-clamp-1 break-anywhere text-xs font-semibold ${isOwn ? 'text-slate-700' : darkMode ? 'text-gray-200' : 'text-slate-600'}`}>
          {original?.senderName || '알 수 없음'}
        </span>
        <span className={`line-clamp-1 break-anywhere text-xs ${isOwn ? 'text-slate-500' : darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
          {originalText}
        </span>
      </button>
    );
  }, [messageById, darkMode, jumpToMessage]);

  // ── MessageItem에 넘기는 안정 콜백들 ──────────────────────────────────────
  // 제스처 상태(롱프레스 타이머·스와이프 잠금)는 여러 ref를 공유하는 한 덩어리라
  // 여기 남기고 이벤트만 위임받는다. 전부 ref/setState만 쓰므로 deps가 비어 있다.

  const registerMessageRef = useCallback((messageId: number, el: HTMLDivElement | null) => {
    if (el) messageRefs.current.set(messageId, el);
    else messageRefs.current.delete(messageId);
  }, []);

  const handleMsgTouchStart = useCallback((msg: Message, isOwn: boolean, e: React.TouchEvent<HTMLDivElement>) => {
    if (selectModeRef.current) return; // 선택 모드: 롱프레스/스와이프 전부 차단 (탭=오버레이 토글)
    if (msg.deletedAt) return;
    const t = e.touches[0];
    touchStartPosRef.current = { x: t.clientX, y: t.clientY };
    const imageId = attachmentIdAt(e.target);
    longPressFiredRef.current = false;
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      // 카카오톡식: 누르고 있는 동안 즉시 오픈. 손가락을 피해 행 아래쪽에 배치
      longPressFiredRef.current = true;
      const start = touchStartPosRef.current;
      const rect = messageRefs.current.get(msg.id)?.getBoundingClientRect();
      navigator.vibrate?.(10);
      setMsgMenu({
        msg,
        isOwn,
        x: start?.x ?? rect?.left ?? 0,
        y: rect ? rect.bottom + 6 : (start?.y ?? 0),
        via: 'touch',
        imageId,
      });
    }, 500);
  }, []);

  const handleMsgTouchMove = useCallback((msg: Message, isOwn: boolean, e: React.TouchEvent<HTMLDivElement>) => {
    if (selectModeRef.current) return;
    if (longPressFiredRef.current) return; // 메뉴 열림 후 이동은 무시 (메뉴 유지)
    // 말풍선 안 표에서 시작한 이동은 표의 가로 스크롤 전용이다. 그대로 두면 표를 옆으로 밀 때마다
    // 스와이프 답장이 걸린다. 롱프레스는 '움직였다'는 사실만으로 취소해야 한다.
    if ((e.target as Element | null)?.closest?.('[data-msg-table]')) {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      return;
    }
    // 10px 슬롭: 미세 떨림으로 long-press가 풀리지 않게, 실제 이동(스크롤)만 취소
    const start = touchStartPosRef.current;
    if (!start) return;
    const t = e.touches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    }
    // 스와이프 답장: 수평 우세(방향 잠금)일 때만 성립. 내 메시지=좌로, 상대=우로.
    // start.x < 24px는 iOS 엣지 백 제스처 영역이라 제외
    if (IS_COARSE_POINTER && !swipeActiveRef.current && !msgMenuOpenRef.current
        && Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy) * 1.5
        && (isOwn ? dx < 0 : dx > 0) && start.x >= 24) {
      swipeActiveRef.current = true;
      swipeMsgIdRef.current = msg.id;
    }
    if (swipeActiveRef.current && swipeMsgIdRef.current === msg.id) {
      const raw = isOwn ? Math.min(0, dx) : Math.max(0, dx);
      const clamped = Math.max(-SWIPE_MAX_PX, Math.min(SWIPE_MAX_PX, raw));
      swipeDxRef.current = clamped;
      // 리렌더 없이 직접 transform (프레임당 setState 방지)
      const el = messageRefs.current.get(msg.id);
      if (el) {
        el.style.transition = '';
        el.style.transform = `translateX(${clamped}px)`;
      }
    }
  }, []);

  const handleMsgTouchEnd = useCallback((msg: Message, e: React.TouchEvent<HTMLDivElement>) => {
    if (selectModeRef.current) return;
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    if (swipeActiveRef.current && swipeMsgIdRef.current === msg.id) {
      const el = messageRefs.current.get(msg.id);
      if (el) {
        el.style.transition = 'transform 0.15s ease-out';
        el.style.transform = '';
      }
      if (Math.abs(swipeDxRef.current) >= SWIPE_TRIGGER_PX) {
        navigator.vibrate?.(10);
        setReplyTo(msg);
      }
      swipeActiveRef.current = false;
      swipeMsgIdRef.current = null;
      swipeDxRef.current = 0;
      e.preventDefault(); // ghost click 방지 (롱프레스 아래와 동일 패턴)
    }
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      // 1차 방어: 지원 브라우저에선 릴리즈 시 compat mouse 이벤트 억제.
      // (억제가 안 되는 브라우저는 ContextMenu의 pointerdown 면역 가드가 커버)
      e.preventDefault();
    }
    touchStartPosRef.current = null;
  }, []);

  const handleMsgTouchCancel = useCallback(() => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressFiredRef.current = false;
    touchStartPosRef.current = null;
    if (swipeActiveRef.current) {
      const el = swipeMsgIdRef.current != null ? messageRefs.current.get(swipeMsgIdRef.current) : null;
      if (el) {
        el.style.transition = 'transform 0.15s ease-out';
        el.style.transform = '';
      }
      swipeActiveRef.current = false;
      swipeMsgIdRef.current = null;
      swipeDxRef.current = 0;
    }
    // setMsgMenu는 건드리지 않음 — touchcancel이 와도 열린 메뉴는 유지
  }, []);

  const handleMsgContextMenu = useCallback((msg: Message, isOwn: boolean, e: React.MouseEvent<HTMLDivElement>) => {
    if (selectModeRef.current) { e.preventDefault(); return; }
    // Android는 long-press 시 네이티브 contextmenu를 발사 → 억제
    // (터치 제스처 중일 때만 — 메뉴는 touchend 경로가 연다)
    if (touchStartPosRef.current) { e.preventDefault(); return; }
    // 데스크톱 우클릭: 커서 위치에 컨텍스트 메뉴
    if (msg.deletedAt) return; // 삭제된 메시지는 브라우저 기본 메뉴 유지
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed) return; // 드래그 선택 중엔 네이티브 복사 메뉴 유지
    e.preventDefault();
    setReactionPickerMsgId(null); // hover 인라인 피커와 동시 오픈 방지
    setMsgMenu({ msg, isOwn, x: e.clientX, y: e.clientY, via: 'mouse', imageId: attachmentIdAt(e.target) });
  }, []);

  const handleReactionPickerToggle = useCallback((messageId: number) => {
    setReactionPickerMsgId(prev => (prev === messageId ? null : messageId));
  }, []);

  // payload 모양은 MemberListModal과 같다. avatar를 빠뜨리면 프로필 사진과 초안 DM 헤더 아바타가 빈다.
  const handleSenderAvatarClick = useCallback((msg: Message) => {
    onOpenProfile?.({
      id: String(msg.senderId),
      username: msg.senderName,
      avatar: msg.senderAvatar || DEFAULT_AVATAR,
      status: 'offline',
    });
  }, [onOpenProfile]);

  // 날짜 구분선은 이전 메시지와의 비교라 메시지마다 Date 객체 2개를 만든다.
  // 목록을 한 번만 훑어 대상 id를 모아두고 MessageItem에는 boolean만 넘긴다.
  const dateSeparatorIds = useMemo(() => {
    const ids = new Set<number>();
    messages.forEach((m, i) => {
      if (i === 0 || isDifferentDate(messages[i - 1].timestamp, m.timestamp)) ids.add(m.id);
    });
    return ids;
  }, [messages]);

  // 메시지 그룹핑: 같은 사람이 5분 안에 이어 보낸 메시지는 아바타·이름·시간을 생략하고 붙인다.
  // 시스템 메시지·삭제 메시지 앞뒤, 날짜 구분선이 있는 자리에서는 그룹을 끊는다.
  const continuationIds = useMemo(() => {
    const ids = new Set<number>();
    const GAP_MS = 5 * 60 * 1000;
    for (let i = 1; i < messages.length; i++) {
      const prev = messages[i - 1];
      const cur = messages[i];
      if (dateSeparatorIds.has(cur.id)) continue;
      if (cur.msgType === 'system' || prev.msgType === 'system') continue;
      if (prev.deletedAt) continue;
      if (String(prev.senderId) !== String(cur.senderId)) continue;
      const dt = new Date(cur.timestamp).getTime() - new Date(prev.timestamp).getTime();
      if (dt < 0 || dt > GAP_MS) continue;
      ids.add(cur.id);
    }
    return ids;
  }, [messages, dateSeparatorIds]);

  // 말풍선 아바타의 접속 점은 여러 사람이 섞이는 방에서만 의미가 있다.
  // DM은 헤더 아바타가 이미 상태를 보여 주고, 나와의 채팅은 참여자가 나뿐이다.
  // ⚠ senderPresence가 없으면(영상통화 사이드바처럼 App 밖에서 마운트된 경우) 점을 아예 그리지 않는다
  //   — 조회 실패를 오프라인으로 폴백하는 규칙 때문에, 빼먹으면 전원이 회색 점으로 보인다.
  const showSenderPresence = !!senderPresence && channel.type !== 'dm' && channel.type !== 'self';
  // 아바타로 프로필 열기는 1:1 대화방에서도 된다(상대가 누군지 보는 것이라 방 종류와 무관). 나와의 채팅은 남이 없다.
  const avatarOpensProfile = !!onOpenProfile && channel.type !== 'self';

  // 본문 텍스트. 엑셀에서 붙여넣은 탭 구분 격자만 표로 바뀌고 나머지는 예전 마크업 그대로다.
  // 셀 안의 링크·멘션 칩·검색어 하이라이트는 highlightText에 그대로 위임한다(단일 출처 유지).
  const renderMessageBody = useCallback((msg: Message, isOwn: boolean) => {
    const blocks = getMessageBlocks(msg.text);
    if (!blocks) {
      return <span className="whitespace-pre-wrap break-anywhere">{highlightText(msg.text, isOwn, msg.mentions)}</span>;
    }
    return (
      <>
        {blocks.map((b, i) => (
          b.type === 'text' ? (
            <span key={i} className="block whitespace-pre-wrap break-anywhere">
              {highlightText(b.text, isOwn, msg.mentions)}
            </span>
          ) : (
            <MessageTable
              key={i}
              rows={b.rows}
              hasHeader={b.hasHeader}
              align={b.align}
              isOwn={isOwn}
              darkMode={darkMode}
              mentions={msg.mentions}
              renderCell={highlightText}
            />
          )
        ))}
      </>
    );
  }, [highlightText, darkMode]);

  // 말풍선 '안' 내용 (전달 라벨 + 답장 인용 + 본문 텍스트).
  // 첨부는 말풍선을 쓰지 않으므로 아래 renderAttachmentBlock이 따로 그린다.
  const renderBubbleContent = useCallback((msg: Message, isOwn: boolean) => (
    <>
      {/* 전달됨 라벨 (원 발신자만 — 출처 채널 미노출, 원문 점프 없음) */}
      {msg.forwardedFrom && (
        <span className={`flex items-center gap-1 text-xs mb-1 ${isOwn ? 'text-slate-500' : darkMode ? 'text-gray-400' : 'text-slate-400'}`}>
          <Forward size={12} className="shrink-0" />
          전달됨 · {msg.forwardedFrom.senderName}
        </span>
      )}
      {/* 답장 원본 인용 미리보기 */}
      {renderReplyQuote(msg, isOwn)}
      {/* 텍스트가 첨부 자리표시 문구가 아닐 때만 표시.
          표가 없는 메시지(대부분)는 getMessageBlocks가 indexOf 한 번으로 null을 돌려주므로
          아래 기존 경로가 그대로 유지된다 — 비용도 DOM도 예전과 같다. */}
      {hasDisplayText(msg.text) && renderMessageBody(msg, isOwn)}
    </>
  ), [darkMode, renderReplyQuote, renderMessageBody]);

  // 말풍선 '밖' 첨부 블록 (이미지 그리드 + 파일 카드)
  const renderAttachmentBlock = useCallback(
    (msg: Message) => renderAttachments(msg.attachments || []),
    [renderAttachments]
  );

  // 메시지 메뉴 '인쇄' 대상 — 메뉴가 열려 있을 때만 계산한다
  const msgMenuPrintUrls = msgMenu ? printableImageUrls(msgMenu.msg, msgMenu.imageId) : [];

  return (
    <>
    {/* 화면 캡처 오버레이 */}
    {isCapturing && capturedScreenshot && (
      <ScreenCaptureOverlay
        screenshotUrl={capturedScreenshot}
        onCapture={(file) => {
          addFiles([file]);
          setIsCapturing(false);
          setCapturedScreenshot(null);
        }}
        onCancel={() => {
          setIsCapturing(false);
          setCapturedScreenshot(null);
        }}
      />
    )}
    <div
      className="flex flex-col flex-1 h-full overflow-hidden relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* 드래그앤드롭 오버레이 */}
      {isDragOver && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-brand-50 border-2 border-dashed border-brand-500 rounded-xl pointer-events-none">
          <div className="flex flex-col items-center gap-2 text-brand-600">
            <Paperclip size={48} />
            <span className="text-lg font-semibold">파일을 여기에 놓으세요</span>
          </div>
        </div>
      )}
      {/* 검색 바 (헤더 돋보기 버튼으로 토글) */}
      {isSearchOpen && (
        <div className={`flex items-center gap-1 px-2 py-1.5 border-b ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-slate-200'}`}>
          <div className="relative flex-1 min-w-0">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (e.shiftKey) {
                    goToPrevResult();
                  } else {
                    goToNextResult();
                  }
                }
              }}
              placeholder="메시지 검색..."
              autoFocus
              className="w-full pl-9 pr-3 py-1.5 text-base bg-slate-100 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
            />
          </div>
          {/* 날짜 점프: 검색어 없이 날짜만 골라도 그 날짜 대화로 이동한다.
              아이콘 클릭 → 숨김 input의 네이티브 피커가 아이콘 위치에 float로 뜬다 */}
          {onJumpToDate && (
            <div className="relative shrink-0">
              <button
                type="button"
                onClick={openDatePicker}
                title="선택한 날짜의 대화로 이동"
                className={`p-1.5 rounded-lg ${jumpDate
                  ? 'text-brand-600 bg-brand-100'
                  : darkMode
                    ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`}
              >
                <CalendarDays size={18} />
              </button>
              <input
                ref={jumpDateInputRef}
                type="date"
                value={jumpDate}
                max={todayStr}
                onChange={(e) => {
                  const value = e.target.value;
                  setJumpDate(value);
                  if (value) onJumpToDate(value);
                }}
                tabIndex={-1}
                aria-hidden="true"
                className={`absolute inset-0 w-full h-full opacity-0 pointer-events-none ${darkMode ? '[color-scheme:dark]' : ''}`}
              />
            </div>
          )}
          {searchResults.length > 0 && (
            <span className="text-xs text-slate-500 whitespace-nowrap">
              {currentSearchIndex + 1} / {searchResults.length}
            </span>
          )}
          {searchQuery && searchResults.length === 0 && (
            <span className="text-xs text-slate-400 whitespace-nowrap">결과 없음</span>
          )}
          <button
            onClick={goToPrevResult}
            disabled={searchResults.length === 0}
            className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
            title="이전 결과"
          >
            <ChevronUp size={16} />
          </button>
          <button
            onClick={goToNextResult}
            disabled={searchResults.length === 0}
            className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
            title="다음 결과"
          >
            <ChevronDown size={16} />
          </button>
          <button
            onClick={closeSearch}
            className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded"
            title="검색 닫기"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* 메시지 영역 */}
      <div className="flex-1 overflow-hidden relative">
      {/* 터치 메뉴 열림 중 overflow-hidden: 백드롭 touch-action은 이미 진행 중인 터치에 무효하므로
          CSS 전환으로 스크롤을 차단 (overflow:hidden도 scroll container라 scrollTop 보존).
          데스크톱 우클릭 메뉴는 휠 스크롤 허용 — ContextMenu의 scroll 리스너가 메뉴를 닫는다 */}
      <div ref={messagesContainerRef} onScroll={handleScroll} className={`h-full ${msgMenu?.via === 'touch' ? 'overflow-hidden' : 'overflow-y-auto overflow-x-hidden'} ${darkMode ? 'p-3 bg-gray-950' : 'p-3 md:p-6'}`}>
      {/* 행 간격은 MessageItem이 그룹 경계(mt-3/5)와 그룹 내부(붙임)를 스스로 결정한다 */}
      <div ref={messagesContentRef} className="space-y-1">
        {/* 빈 방 안내 — 메시지가 정말 없는 방(lastMessageAt 없음)에서만. 채널 전환 직후 로딩 중 깜빡임을
            피하려고 "서버가 마지막 메시지가 있다고 알려준 방"은 대상에서 뺀다. 과거 열람 창(hasMore/hasMoreNewer)도 제외 */}
        {messages.length === 0 && !hasMore && !hasMoreNewer && !channel.lastMessageAt && (
          <div className="flex flex-col items-center justify-center text-center px-6 py-16 md:py-24 select-none">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${darkMode ? 'bg-gray-800 text-gray-500' : 'bg-brand-100 text-brand-600'}`}>
              {channel.type === 'self' ? <Bookmark size={28} /> : <MessageCircle size={28} />}
            </div>
            <p className={`text-base font-semibold ${darkMode ? 'text-gray-200' : 'text-slate-700'}`}>
              {channel.type === 'self'
                ? '나만 보는 공간입니다'
                : channel.type === 'dm'
                  ? `${channel.name || '상대방'}님과의 첫 대화입니다`
                  : channel.type === 'public'
                    ? '아직 올라온 공지가 없습니다'
                    : '아직 메시지가 없습니다'}
            </p>
            <p className={`mt-1.5 text-sm max-w-xs ${darkMode ? 'text-gray-500' : 'text-slate-400'}`}>
              {channel.type === 'self'
                ? '메모나 파일을 나에게 보내 보관하세요. 다른 기기에서도 그대로 보입니다.'
                : channel.type === 'dm'
                  ? '아래 입력창에서 첫 메시지를 보내 보세요.'
                  : channel.type === 'public'
                    ? '관리자가 공지를 올리면 이곳에 표시됩니다.'
                    : channel.memberCount && channel.memberCount > 1
                      ? `멤버 ${channel.memberCount}명이 함께합니다. 첫 메시지를 보내 대화를 시작해 보세요.`
                      : '첫 메시지를 보내 대화를 시작해 보세요.'}
            </p>
          </div>
        )}
        {hasMore && (
          <div className="flex justify-center py-2">
            <button
              onClick={async () => {
                if (!onLoadMore || isLoadingMore) return;
                const container = messagesContainerRef.current;
                prevScrollHeightRef.current = container?.scrollHeight || 0;
                skipAutoScrollRef.current = true;
                setIsLoadingMore(true);
                await onLoadMore();
                setIsLoadingMore(false);
              }}
              disabled={isLoadingMore}
              className="px-4 py-2 text-sm text-slate-500 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
            >
              {isLoadingMore ? '불러오는 중...' : '이전 메시지 불러오기'}
            </button>
          </div>
        )}
        {messages.map((msg) => {
          const isOwn = String(msg.senderId) === String(currentUser.id);
          return (
            <MessageItem
              key={msg.id}
              msg={msg}
              isOwn={isOwn}
              isDeleted={!!msg.deletedAt}
              isHighlighted={highlightedMessageId === msg.id}
              showDateSeparator={dateSeparatorIds.has(msg.id)}
              isContinuation={continuationIds.has(msg.id)}
              // 내 아바타에는 점을 붙이지 않는다(항상 접속 중이라 의미가 없다).
              // onlineUsers에는 오프라인이 들어오지 않으므로 조회 실패가 곧 오프라인이다.
              senderStatus={
                showSenderPresence && !isOwn
                  ? senderPresence?.get(String(msg.senderId)) ?? 'offline'
                  : undefined
              }
              darkMode={darkMode}
              isPublicChannel={channel.type === 'public'}
              currentUserId={String(currentUser.id)}
              currentUserAvatar={currentUser.avatar}
              selectMode={selectMode}
              isSelected={selectedIds.has(msg.id)}
              isMenuTarget={msgMenu?.msg.id === msg.id}
              isReactionPickerOpen={reactionPickerMsgId === msg.id}
              registerRef={registerMessageRef}
              renderBubbleContent={renderBubbleContent}
              renderAttachmentBlock={renderAttachmentBlock}
              onToggleSelect={toggleSelect}
              onReply={setReplyTo}
              onReactionToggle={onReactionToggle}
              onReactionPickerToggle={handleReactionPickerToggle}
              onTouchStart={handleMsgTouchStart}
              onTouchMove={handleMsgTouchMove}
              onTouchEnd={handleMsgTouchEnd}
              onTouchCancel={handleMsgTouchCancel}
              onContextMenu={handleMsgContextMenu}
              onAvatarClick={avatarOpensProfile && !isOwn ? handleSenderAvatarClick : undefined}
            />
          );
        })}
        {/* 날짜 점프로 과거를 보고 있을 때만 노출되는 최신 방향 페이징 */}
        {hasMoreNewer && (
          <div className="flex justify-center py-2">
            <button
              onClick={async () => {
                if (!onLoadNewer || isLoadingNewer) return;
                setIsLoadingNewer(true);
                await onLoadNewer();
                setIsLoadingNewer(false);
              }}
              disabled={isLoadingNewer}
              className="px-4 py-2 text-sm text-slate-500 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
            >
              {isLoadingNewer ? '불러오는 중...' : '다음 메시지 불러오기'}
            </button>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      </div>

      {/* 최근 채팅으로 이동 버튼 (과거 열람 중이면 최신 창을 다시 불러온다) */}
      {(!isAtBottom || hasMoreNewer) && (
        <button
          onClick={() => {
            if (hasMoreNewer && onReturnToLatest) {
              onReturnToLatest();
              return;
            }
            scrollToBottom(true);
            setIsAtBottom(true);
          }}
          className="absolute bottom-4 right-4 flex items-center gap-1.5 bg-white/90 backdrop-blur-sm shadow-lg rounded-full px-4 py-2 text-sm text-slate-600 hover:bg-white hover:shadow-xl transition-all duration-200 z-10 border border-slate-200"
        >
          <ArrowDown size={16} />
          {hasMoreNewer ? '최근 대화로' : '최근 채팅'}
        </button>
      )}

      {/* 타이핑 표시 — absolute 오버레이라 레이아웃 시프트 없음 (isAtBottom 스크롤 로직 보호) */}
      {typingUsers.length > 0 && (
        <div className="absolute bottom-2 left-3 z-10 flex items-center gap-2 bg-white/90 backdrop-blur-sm border border-slate-200 shadow-sm rounded-full px-3 py-1.5 pointer-events-none">
          <div className="typing-indicator"><span /><span /><span /></div>
          <span className="text-xs text-slate-500">
            {typingUsers.length === 1
              ? `${typingUsers[0]}님이 입력 중`
              : `${typingUsers[0]}님 외 ${typingUsers.length - 1}명이 입력 중`}
          </span>
        </div>
      )}
      </div>

      {/* 입력 영역 */}
      <div className={`p-2 pb-3 md:px-6 md:pt-3 md:pb-4 safe-area-bottom chat-input-bottom ${darkMode ? 'bg-gray-900 border-t border-gray-700' : 'bg-slate-50'}`}>
        {/* 예약 메시지 칩 — 이 방에 내 예약(대기·실패)이 있을 때만. 실패 건은 색으로 알린다 */}
        {!selectMode && channelScheduled.length > 0 && (
          <button
            type="button"
            onClick={() => openScheduleModal(false)}
            className={`flex items-center gap-1.5 mb-2 px-3 py-1.5 rounded-lg text-xs transition-colors ${
              hasFailedScheduled
                // brand-50은 slate-50 캔버스에서 사실상 안 보인다(디자인 토큰 규칙) — 틴트는 brand-100부터
                ? 'bg-brand-100 text-brand-700 font-semibold hover:bg-brand-200'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <Clock size={13} aria-hidden="true" />
            예약 메시지 {channelScheduled.length}건
            {hasFailedScheduled && ' · 보내지 못한 예약 있음'}
          </button>
        )}
        {/* 편집 중 표시 배너 (답장과 상호 배타) */}
        {!selectMode && editingMsg && (
          <div className={`flex items-center gap-2 mb-2 px-3 py-2 rounded-lg border-l-2 border-brand-600 ${darkMode ? 'bg-gray-800' : 'bg-slate-50'}`}>
            <Pencil size={16} className="text-brand-600 shrink-0" />
            <div className="flex flex-col min-w-0 flex-1">
              <span className={`text-xs font-semibold truncate ${darkMode ? 'text-gray-200' : 'text-slate-600'}`}>
                메시지 수정 중
              </span>
              <span className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                {editingMsg.text}
              </span>
            </div>
            <button
              type="button"
              onClick={cancelEdit}
              className="p-1 text-slate-400 hover:text-brand-600 hover:bg-brand-50 rounded transition-colors shrink-0"
              title="수정 취소"
            >
              <X size={16} />
            </button>
          </div>
        )}
        {/* 답장 대상 표시 배너 */}
        {!selectMode && replyTo && (
          <div className={`flex items-center gap-2 mb-2 px-3 py-2 rounded-lg border-l-2 border-brand-600 ${darkMode ? 'bg-gray-800' : 'bg-slate-50'}`}>
            <Reply size={16} className="text-brand-600 shrink-0" />
            <div className="flex flex-col min-w-0 flex-1">
              <span className={`text-xs font-semibold truncate ${darkMode ? 'text-gray-200' : 'text-slate-600'}`}>
                {replyTo.senderName}님에게 답장
              </span>
              <span className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                {replyTo.deletedAt
                  ? '삭제된 메시지입니다'
                  : (replyTo.text && replyTo.text !== '파일을 공유했습니다.')
                    ? replyTo.text
                    : '첨부파일'}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setReplyTo(null)}
              className="p-1 text-slate-400 hover:text-brand-600 hover:bg-brand-50 rounded transition-colors shrink-0"
              title="답장 취소"
            >
              <X size={16} />
            </button>
          </div>
        )}
        {/* 첨부파일 미리보기 */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3 p-2 bg-slate-50 rounded-lg border border-slate-200">
            {attachments.map(attachment => {
              // 비이미지는 썸네일을 만들지 않으므로(base64 메모리 절약) 확장자 아이콘으로 대신한다
              const TypeIcon = iconForFileName(attachment.name);
              return (
              <div
                key={attachment.id}
                className="relative group flex items-center gap-2 bg-white border border-slate-200 rounded-lg p-2 pr-8"
              >
                {attachment.thumbnail ? (
                  <img
                    src={attachment.thumbnail}
                    alt={attachment.name}
                    className="w-12 h-12 object-cover rounded"
                  />
                ) : (
                  <div className="w-12 h-12 flex items-center justify-center bg-slate-100 rounded">
                    <TypeIcon size={24} className="text-slate-400" />
                  </div>
                )}
                <div className="flex flex-col min-w-0">
                  <span className="text-sm text-slate-700 truncate max-w-[120px]">
                    {attachment.name}
                  </span>
                  <span className="text-xs text-slate-400">
                    {formatFileSize(attachment.size)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => removeAttachment(attachment.id)}
                  aria-label={`${attachment.name} 첨부 제거`}
                  className="absolute top-1 right-1 p-1.5 text-slate-400 hover:text-brand-600 hover:bg-brand-50 rounded transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
              );
            })}
            {retentionNotice && (
              <p className="w-full text-xs text-slate-500">{retentionNotice}</p>
            )}
          </div>
        )}

        {/* 업로드 프로그레스 바 */}
        {isUploading && (
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-500">업로드 중...</span>
              <span className="text-xs font-medium text-brand-700">{uploadProgress}%</span>
            </div>
            <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-600 rounded-full transition-all duration-200"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {selectMode ? (
          /* 선택 모드 액션바 — 입력 영역을 조건부 대체 */
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-slate-700'}`}>
              {selectedIds.size}개 선택됨
              {selectedIds.size >= MAX_FORWARD_SELECT && (
                <span className="ml-1 text-xs text-amber-600">(최대)</span>
              )}
            </span>
            <div className="flex-1" />
            <button
              type="button"
              onClick={exitSelectMode}
              className={`px-3 py-2 text-sm rounded-lg transition-colors ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-slate-500 hover:bg-slate-100'}`}
            >
              취소
            </button>
            <button
              type="button"
              disabled={selectedIds.size === 0}
              onClick={() => {
                // 시간순(id 오름차순) 전달 — 대상 채널에 원본 순서대로 도착
                const msgs = messages
                  .filter(m => selectedIds.has(m.id))
                  .sort((a, b) => Number(a.id) - Number(b.id));
                exitSelectMode();
                if (msgs.length > 0) onForward?.(msgs);
              }}
              className="flex items-center gap-1.5 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Forward size={16} />
              전달 ({selectedIds.size})
            </button>
          </div>
        ) : isReadOnly ? (
          <div className="flex items-center justify-center p-4 bg-slate-100 border border-slate-200 rounded-xl text-slate-500 text-sm">
            공지 전용 채널입니다. 관리자만 관리자 콘솔에서 글을 올릴 수 있습니다.
          </div>
        ) : (
          <div className="relative">
            {/* 멘션 자동완성 팝업 (입력창 위 오버레이 — 이모지 피커 패턴) */}
            {mentionQuery !== null && (
              <MentionAutocomplete
                candidates={mentionCandidates}
                activeIndex={Math.min(mentionIndex, Math.max(0, mentionCandidates.length - 1))}
                onSelect={selectMentionCandidate}
                onHoverIndex={setMentionIndex}
                darkMode={darkMode}
                loading={membersLoading}
              />
            )}
            {/* 비멤버 멘션 인라인 고지 */}
            {(outsiderNotice || tableNotice) && (
              <div className={`mb-1.5 px-3 py-1.5 rounded-lg text-xs ${darkMode ? 'bg-gray-800 text-amber-400' : 'bg-amber-50 text-amber-700'}`}>
                {outsiderNotice || tableNotice}
              </div>
            )}
            <form
              onSubmit={handleSubmit}
              className={`flex flex-col gap-1 p-1 min-h-[48px] rounded-2xl focus-within:ring-2 focus-within:ring-brand-400 focus-within:border-transparent transition-all ${darkMode ? 'bg-gray-800 border border-gray-600' : 'bg-white border border-slate-200 shadow-sm'}`}
            >
              {/* 붙여넣은 표와 그 위에 쓴 문구 — 입력창에 보이는 순서가 곧 전송되는 순서다.
                  표가 없으면(절대다수) composerAbove는 빈 배열이라 아래 액션 행만 남고,
                  자식이 하나뿐인 column flex는 예전 한 줄 레이아웃과 박스가 같다.
                  ⚠ 아래 액션 행 wrapper는 조건 없이 항상 렌더한다 — 조건부로 만들면 표가 생기는 순간
                    서브트리가 리마운트되어 textarea가 포커스·캐럿·한글 조합을 잃는다. */}
              {composerAbove}
              <div className="flex items-end gap-1">
              {/* 숨겨진 파일 입력 */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              {/* 숨겨진 이미지 전용 입력 */}
              <input
                ref={imageFileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />

              {/* 첨부 버튼 (통합 드롭다운) */}
              <div className="relative" ref={imageMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowImageMenu(!showImageMenu)}
                  className={`p-1.5 min-h-[40px] min-w-[40px] flex items-center justify-center rounded-lg transition-colors ${
                    showImageMenu
                      ? darkMode ? 'text-brand-400 bg-gray-700' : 'text-brand-600 bg-brand-50'
                      : darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200'
                  }`}
                  title="첨부"
                >
                  <Paperclip size={20} />
                </button>

                {showImageMenu && (
                  <div className={`absolute bottom-full left-0 mb-2 w-40 rounded-lg shadow-lg z-50 overflow-hidden ${darkMode ? 'bg-gray-800 border border-gray-600' : 'bg-white border border-slate-200'}`}>
                    <button
                      type="button"
                      onClick={() => {
                        fileInputRef.current?.click();
                        setShowImageMenu(false);
                      }}
                      className={`w-full px-4 py-2.5 text-sm text-left flex items-center gap-2 ${darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-slate-700 hover:bg-slate-100'}`}
                    >
                      <Paperclip size={16} />
                      파일 첨부
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        imageFileInputRef.current?.click();
                        setShowImageMenu(false);
                      }}
                      className={`w-full px-4 py-2.5 text-sm text-left flex items-center gap-2 ${darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-slate-700 hover:bg-slate-100'}`}
                    >
                      <ImageIcon size={16} />
                      이미지 선택
                    </button>
                    {CAN_SCREEN_CAPTURE && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowImageMenu(false);
                          startScreenCapture();
                        }}
                        className={`w-full px-4 py-2.5 text-sm text-left flex items-center gap-2 ${darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-slate-700 hover:bg-slate-100'}`}
                      >
                        <Monitor size={16} />
                        화면 캡처
                      </button>
                    )}
                    {/* 예약 전송 — 주 진입점은 전송 버튼 롱프레스/우클릭, 여기는 발견성 보조 */}
                    {scheduleEnabled && !editingMsg && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowImageMenu(false);
                          openScheduleModal(true);
                        }}
                        className={`w-full px-4 py-2.5 text-sm text-left flex items-center gap-2 ${darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-slate-700 hover:bg-slate-100'}`}
                      >
                        <Clock size={16} />
                        예약 전송
                      </button>
                    )}
                    {/* 일정 공유 — 새 일정을 만들며 이 대화방에 카드로 보낸다(참석자 = 이 방 사람). 초안 DM은 방이 없어 숨긴다 */}
                    {!!channel.id && !editingMsg && !isReadOnly && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowImageMenu(false);
                          openCalendarEditorHere();
                        }}
                        className={`w-full px-4 py-2.5 text-sm text-left flex items-center gap-2 ${darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-slate-700 hover:bg-slate-100'}`}
                      >
                        <CalendarPlus size={16} />
                        일정 공유
                      </button>
                    )}
                  </div>
                )}
              </div>

              <textarea
                ref={textareaRef}
                value={bottom.value}
                onFocus={(e) => {
                  composerFocusRef.current = { kind: 'textarea', blockIndex: bottom.mode === 'block' ? bottom.index : null, el: e.currentTarget };
                }}
                onChange={(e) => {
                  const next = composeFromBottom(e.target.value);
                  setInputValue(next);
                  emitTyping(next);
                  updateMentionQuery(e.target);
                }}
                onKeyDown={handleKeyDown}
                // 한글 조합 확정 시점에 멘션 필터 재평가 (조합 중 onChange 값은 미확정)
                onCompositionEnd={(e) => updateMentionQuery(e.currentTarget)}
                // 커서 이동(클릭·화살표)으로 @토큰을 벗어나면 팝업 닫기
                onClick={(e) => updateMentionQuery(e.currentTarget)}
                onKeyUp={(e) => {
                  if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) updateMentionQuery(e.currentTarget);
                }}
                onPaste={(e) => handlePaste(e, bottom.mode === 'block' ? bottom.index : null)}
                placeholder={
                  channel.type === 'dm'
                    ? `${channel.name || '상대방'}님에게 메시지 보내기`
                    : channel.type === 'self'
                      ? '나에게 메모 남기기'
                      : `#${channel.name || '채널'}에 메시지 보내기`
                }
                className={`flex-1 bg-transparent border-none focus:outline-none focus:ring-0 resize-none max-h-32 py-2 px-1.5 text-base md:text-sm ${darkMode ? 'text-gray-100 placeholder-gray-500' : 'text-slate-700 placeholder-slate-400'}`}
                rows={1}
                style={{ minHeight: '40px' }}
              />

              {/* 이모지 피커 컨테이너 */}
              <div className="relative" ref={emojiPickerRef}>
                <button
                  type="button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className={`p-1.5 min-h-[40px] min-w-[40px] flex items-center justify-center rounded-lg transition-colors ${showEmojiPicker
                    ? darkMode ? 'text-brand-400 bg-gray-700' : 'text-brand-600 bg-brand-50'
                    : darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                    }`}
                  title="이모지"
                >
                  <Smile size={20} />
                </button>

                {/* 이모지 피커 팝업 */}
                {showEmojiPicker && (
                  <div className="absolute bottom-full right-0 mb-2 z-50 max-w-[calc(100vw-1rem)]">
                    <Suspense fallback={null}>
                      <EmojiPicker
                        onEmojiClick={handleEmojiClick}
                        theme={'light' as Theme}
                        // 피커 격자를 OS 이모지 폰트로 렌더 — 기본값(APPLE)은 CDN의 애플 PNG라
                        // 안드로이드에서 입력창(Noto Color Emoji)과 다르게 보인다
                        emojiStyle={'native' as EmojiStyle}
                        width={Math.min(320, window.innerWidth - 32)}
                        height={350}
                        searchPlaceholder="이모지 검색..."
                        previewConfig={{ showPreview: false }}
                      />
                    </Suspense>
                  </div>
                )}
              </div>

              <button
                ref={sendButtonRef}
                type="submit"
                aria-label="메시지 보내기"
                disabled={isUploading || (!inputValue.trim() && attachments.length === 0)}
                {...(scheduleEnabled && !editingMsg ? sendLongPressHandlers : {})}
                onContextMenu={(e) => {
                  // useLongPress의 onContextMenu(터치 제스처 중 네이티브 메뉴 억제)를 먼저 태운다 —
                  // preventDefault 되었으면 터치 경로이므로 데스크톱 우클릭 메뉴를 열지 않는다 (ChannelListItem 패턴)
                  if (scheduleEnabled && !editingMsg) sendLongPressHandlers.onContextMenu(e);
                  if (e.defaultPrevented || !scheduleEnabled || editingMsg) return;
                  e.preventDefault();
                  setSendMenu({ x: e.clientX, y: e.clientY, via: 'mouse' });
                }}
                // touch-callout-none: 롱프레스(예약 전송) 중 iOS가 홀드를 텍스트 선택으로
                // 해석해 돋보기를 띄우는 것 방지 — ChannelListItem·MessageItem과 같은 패턴
                className="p-1.5 min-h-[40px] min-w-[40px] flex items-center justify-center bg-brand-600 text-white rounded-xl hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors touch-callout-none"
              >
                {isUploading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send size={20} />
                )}
              </button>
              </div>
            </form>
            {!IS_COARSE_POINTER && (
              <p className={`hidden md:block text-xs text-center mt-1 ${darkMode ? 'text-gray-600' : 'text-slate-400'}`}>
                Enter 전송 · Shift+Enter 줄바꿈
              </p>
            )}
          </div>
        )}
      </div>
    </div>
    {/* 이미지 라이트박스 (인앱 뷰어) */}
    <ImageLightbox
      open={lightbox.open}
      slides={lightbox.slides}
      index={lightbox.index}
      onClose={() => setLightbox((s) => ({ ...s, open: false }))}
      onIndexChange={(i) => setLightbox((s) => ({ ...s, index: i }))}
      onDownload={(slide) => saveImageNative(slide.downloadUrl, slide.downloadFilename)}
      onPrint={(slide) => {
        printImages([slide.downloadUrl]).catch(() => showToast({ type: 'error', message: PRINT_IMAGE_FAILED_TOAST }));
      }}
    />
    {/* 전송 버튼 롱프레스/우클릭 메뉴 — 예약 전송 진입점 */}
    {sendMenu && (
      <ContextMenu
        x={sendMenu.x}
        y={sendMenu.y}
        variant={darkMode ? 'dark' : 'light'}
        backdrop={sendMenu.via === 'touch'}
        onClose={() => setSendMenu(null)}
        items={[
          {
            key: 'schedule',
            label: '예약 전송',
            icon: Clock,
            onSelect: () => {
              setSendMenu(null);
              openScheduleModal(true);
            },
          },
        ]}
      />
    )}
    {/* 메시지 예약 — 작성(본문·사진·파일) + 보낼 시각 + 이 방의 예약 목록. 열 때마다 새로 마운트된다 */}
    {scheduleModal && (
      <ScheduleComposerModal
        channelTitle={channel.name || ''}
        items={channelScheduled}
        seed={scheduleModal.seed}
        onCreate={handleScheduleCreate}
        onUpdate={handleScheduleUpdate}
        onCancelItem={(item) => onCancelScheduledMessage?.(item)}
        dropSinkRef={scheduleDropSinkRef}
        onClose={() => setScheduleModal(null)}
      />
    )}
    {/* 메시지 플로팅 컨텍스트 메뉴 (모바일 long-press + 데스크톱 우클릭 공용) */}
    {msgMenu && (
      <ContextMenu
        x={msgMenu.x}
        y={msgMenu.y}
        variant={darkMode ? 'dark' : 'light'}
        backdrop={msgMenu.via === 'touch'}
        onClose={() => setMsgMenu(null)}
        header={
          <div className="flex gap-0.5">
            {REACTION_EMOJIS.map(emoji => (
              <button
                key={emoji}
                onClick={() => {
                  onReactionToggle?.(msgMenu.msg.id, emoji);
                  setMsgMenu(null);
                }}
                className={`rounded p-1 text-lg transition-colors ${darkMode ? 'hover:bg-chrome' : 'hover:bg-slate-100'}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        }
        items={[
          {
            key: 'reply',
            label: '답장',
            icon: Reply,
            onSelect: () => {
              setReplyTo(msgMenu.msg);
              setMsgMenu(null);
            },
          },
          ...(msgMenu.msg.text && msgMenu.msg.text !== '파일을 공유했습니다.'
            ? [{
                key: 'copy',
                label: '복사',
                icon: Copy,
                onSelect: () => {
                  // 1열 표의 부호화 탭을 벗겨서 준다 — 안 벗기면 엑셀에 다시 붙일 때 빈 B열이 생긴다
                  navigator.clipboard?.writeText(textForClipboard(msgMenu.msg.text)).catch(() => {});
                  setMsgMenu(null);
                },
              }]
            : []),
          ...(msgMenuPrintUrls.length > 0
            ? [{
                key: 'print',
                label: msgMenuPrintUrls.length > 1 ? `사진 ${msgMenuPrintUrls.length}장 인쇄` : '인쇄',
                icon: Printer,
                onSelect: () => {
                  const urls = msgMenuPrintUrls;
                  setMsgMenu(null);
                  printImages(urls).catch(() => showToast({ type: 'error', message: PRINT_IMAGE_FAILED_TOAST }));
                },
              }]
            : []),
          ...(onForward && msgMenu.msg.msgType !== 'system'
            ? [{
                key: 'forward',
                label: '전달',
                icon: Forward,
                onSelect: () => {
                  const msg = msgMenu.msg;
                  setMsgMenu(null);
                  onForward([msg]);
                },
              }, {
                key: 'forward-multi',
                label: '선택하여 전달',
                icon: ListChecks,
                onSelect: () => {
                  const msg = msgMenu.msg;
                  setMsgMenu(null);
                  setSelectMode(true);
                  setSelectedIds(new Set(msg.deletedAt ? [] : [msg.id]));
                },
              }]
            : []),
          ...(!!channel.id && !isReadOnly && msgMenu.msg.msgType !== 'system' && !msgMenu.msg.calendarEvent
            && msgMenu.msg.text && msgMenu.msg.text !== '파일을 공유했습니다.'
            ? [{
                key: 'calendar',
                label: '일정 만들기',
                icon: CalendarPlus,
                onSelect: () => {
                  const msg = msgMenu.msg;
                  setMsgMenu(null);
                  openCalendarEditorHere(msg);
                },
              }]
            : []),
          ...(msgMenu.isOwn
            && (!msgMenu.msg.attachments || msgMenu.msg.attachments.length === 0)
            && msgMenu.msg.text
            && !msgMenu.msg.calendarEvent
            ? [{
                key: 'edit',
                label: '수정',
                icon: Pencil,
                onSelect: () => {
                  const msg = msgMenu.msg;
                  setMsgMenu(null);
                  startEdit(msg);
                },
              }]
            : []),
          ...(onSetNotice && msgMenu.msg.text && msgMenu.msg.text !== '파일을 공유했습니다.'
            ? [{
                key: 'notice',
                label: '공지로 등록',
                icon: Megaphone,
                onSelect: () => {
                  const id = msgMenu.msg.id;
                  setMsgMenu(null);
                  onSetNotice(id);
                },
              }]
            : []),
          ...(msgMenu.isOwn
            ? [{
                key: 'delete',
                label: '삭제',
                icon: Trash2,
                danger: true,
                onSelect: () => {
                  const id = msgMenu.msg.id;
                  setMsgMenu(null);
                  handleDelete(id);
                },
              }]
            : []),
        ]}
      />
    )}

    <ConfirmDialog
      isOpen={deleteTargetId !== null}
      title="메시지 삭제"
      message="이 메시지를 삭제하시겠습니까?"
      confirmLabel="삭제"
      danger
      onConfirm={confirmDelete}
      onCancel={() => setDeleteTargetId(null)}
    />
    </>
  );
};
export const ChatWindow = React.memo(ChatWindowComponent);
